// ============================================================================
// FSA NOTE MODAL — MULTI-COLUMN CLASS TABLE (Option A foundation, Phase 2)
// ============================================================================
// Renders the per-class table for schedules whose column_spec has more
// than the standard CY/PY pair (e.g. NOTE_PPE's 10-column movement
// layout: gross_opening / additions / deletions / gross_closing /
// dep_opening / … / net_closing / net_py).
//
// Layout: horizontal scroll table; the label column is sticky-left so
// the auditor never loses class context while scrolling the amount
// columns. Header is sticky-top too — matches SyutGrid chrome.
//
// Cells:
//   • is_computed = true → read-only; auto-evaluated from the inline
//                          `formula` against the row's column_values
//                          on every keystroke. Persisted on save.
//   • amount input        → NumericInput, right-aligned, tabular-nums.
//                          Updates the workspace's column_values map
//                          atomically (write input + recompute every
//                          dependent computed cell in the same row).
//   • is_total tag        → bold + accent background. Drives the BS
//                          NOTE_TOTAL value and the drift validator.
//   • is_prior_year tag   → muted text colour.
//
// Total row at the bottom sums every amount column across visible
// classes. The is_total column's sum is the Note's closing total — same
// number the BS reads.
//
// This component is rendered IN PLACE OF MainNotePane's standard CY/PY
// class table. MainNotePane's other surfaces (Reconciliation, Additional
// Disclosures, Notes Text, Drift) continue to render around it.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ActionMenu, type ActionMenuItem } from 'components/ui';
import { NumericInput } from '../../shared/NumericInput';
import { autoRound } from '../../shared/rounding';
import { evaluateFormula } from './formulaEvaluator';
import { ROUNDED_SUFFIX } from './noteClassDataResolver';
import SharedLineItemEditor, {
  blankLineItemDraft,
  type LineItemDraft,
} from '../shared/LineItemEditor';
import {
  DriftChip,
  CustomClassChip,
  HiddenInPrintChip,
  RollupChip,
  SourceChip,
} from '../../shared/RowBadges';
import type { FormatColumnSpec } from 'api/types/fsa/template.types';
import type {
  NoteAddedClass,
  NoteClassData,
  NoteClassLive,
  NoteGroupingDimension,
  NoteLedger,
  NoteLineItem,
  NoteWorkspaceData,
} from 'api/types/fsa/noteData.types';

interface Props {
  /** Parsed column_spec from the schedule (already parsed at the call site). */
  columnSpec: FormatColumnSpec[];
  /** Template classes for the schedule from the live TB read. */
  classes: NoteClassLive[];
  /**
   * Engagement-time custom classes from <c>workspace.added_classes</c>.
   * Merged into the rows list alongside template classes so the
   * auditor's "+ Add Custom Class" entries render in PPE / Intangibles
   * the same way they do on standard 2-col notes.
   */
  addedClasses: NoteAddedClass[];
  /** Workspace under edit. */
  workspace: NoteWorkspaceData;
  /** Engagement rounding factor key — drives the rounded-companion auto-recompute on each edit. */
  roundingFactorKey: string;
  /**
   * Display-mode toggle propagated from MainNotePane's toolbar.
   * - 'actual'  → cells show / edit the actual column_values entries.
   * - 'rounded' → cells show the `<code>__rounded` companions instead.
   *               Read-only in rounded mode for now; the actual values
   *               remain canonical and the rounded view is derived.
   */
  displayMode: 'actual' | 'rounded';
  /**
   * Atomically writes a single field on a class's workspace entry.
   * Same callback MainNotePane uses for the 2-col path; we use it to
   * persist `column_values` updates (read-modify-write the dict).
   */
  setClassField: <K extends keyof NoteClassData>(
    classCode: string,
    field: K,
    value: NoteClassData[K],
  ) => void;
  /** Pulls the latest live TB snapshot for a single class into class_data. */
  applyLedgerSnapshot: (classCode: string) => void;
  /** Toggles workspace.class_data[code].hide_in_print. */
  setClassHideInPrint: (classCode: string, hide: boolean) => void;
  /**
   * Opens the shared <see cref="ClassLabelEditor"/> in 'rename-template'
   * mode for this class. Same editor the standard pane uses — driven
   * by MainNotePane's customClassDraft state. Save commits the
   * per-engagement label override; Cancel discards.
   */
  onStartRenameTemplate: (classCode: string, currentLabel: string) => void;
  /** Writes the full line_items array for a class. Used for add / edit / delete on the multi-col line-item rows. */
  setLineItems: (classCode: string, lineItems: NoteLineItem[]) => void;
  /** Codes for which custom-class actions (Rename / Delete) apply. Renamed via the shared editor; deleted via the action menu. */
  customClassCodes: Set<string>;
  /** Opens the shared editor in 'rename' mode for a custom class. */
  onStartRenameCustom: (localId: string, currentName: string) => void;
  /** Deletes a custom class entirely (the row + its class_data). */
  deleteCustomClass: (localId: string) => void;
  /** Optional grouping dimension parsed from the schedule's grouping_dimension JSON. NULL when the schedule isn't grouped — dimension setter actions hide. */
  groupingDimension?: NoteGroupingDimension | null;
  /** Persists a class-level dimension_values entry. NULL clears the dimension. */
  setClassDimensionValue: (classCode: string, dimCode: string, value: string | null) => void;
  /**
   * Writes one column's value into <c>workspace.prior_year_totals</c>. The
   * <c>columnCode</c> arg is the actual key when editing in actual mode and
   * the <c>__rounded</c> companion when editing in rounded mode (same
   * convention as the per-class column_values writes). Passing
   * <c>null</c> clears the entry.
   */
  setPriorYearTotal: (columnCode: string, value: number | null) => void;
  /**
   * Pushes the freshly-computed per-column Total dict up to
   * <c>workspace.column_totals</c>. Called from a useEffect inside this
   * component whenever the underlying class data changes — keeps the
   * persisted Total row in lock-step with what the UI footer shows.
   * The PDF renderer reads <c>column_totals</c> verbatim with no sum
   * recomputation server-side, satisfying the FSA save-compute-display-
   * read policy.
   */
  setColumnTotals: (totals: Record<string, number>) => void;
  readOnly: boolean;
  /**
   * Raw schedule meta_data JSON — parsed for `display_options.py_layout`.
   *   • "inline_columns" (default, PPE pattern) — render the inline
   *     "Previous Year Total" row in tfoot.
   *   • "separate_grid" — hide the inline PY row; a second matrix block
   *     for PY renders below the CY block (planned v2).
   * Empty / null falls back to "inline_columns" so PPE keeps working.
   */
  scheduleMetaDataJson?: string | null;
  /**
   * TRUE for credit-balance schedules (liability / equity / income).
   * The matrix auditor types positive amounts; the reconciliation
   * target `class_data[reconcileWithClass].amount_actual` is stored
   * signed (negative on credit-balance schedules). The recon chip
   * sign-flips the target before comparison so a matched matrix
   * doesn't surface a phantom drift of 2 × the value.
   */
  isCreditBalance?: boolean;
  /**
   * Section-grid band mode: when set, this grid is one collapsible
   * `AgeingSectionBand` rendering a SINGLE class (the band header already
   * names the section, so the in-table section banner is suppressed). The
   * note-wide column_totals write is skipped (MainNotePane computes the
   * Σ-of-all-classes total centrally so the two band instances don't
   * overwrite each other). Used by the Owners' Capital Account Note's
   * Partners' Capital / Current Account section bands.
   */
  bandClassCode?: string;
  /**
   * TRUE when there is NO prior-year-linked engagement (first-year / not
   * roll-forward-linked). A plain `is_prior_year` column (e.g. the Owners'
   * Capital Account Note's Opening Balance) is normally read-only in actuals
   * — sourced from the prior engagement via "Import from Previous Year". With
   * no PY linkage there is nothing to import, so the auditor must be able to
   * type the opening balance directly. Formula-driven is_prior_year columns
   * (PPE Net Block PY) are unaffected — they stay calculated regardless.
   */
  allowPriorYearInput?: boolean;
}

const MultiColumnClassTable: React.FC<Props> = ({
  columnSpec,
  classes,
  addedClasses,
  workspace,
  roundingFactorKey,
  displayMode,
  setClassField,
  applyLedgerSnapshot,
  setClassHideInPrint,
  onStartRenameTemplate,
  setLineItems,
  customClassCodes,
  onStartRenameCustom,
  deleteCustomClass,
  groupingDimension,
  setClassDimensionValue,
  setPriorYearTotal,
  setColumnTotals,
  readOnly,
  scheduleMetaDataJson,
  isCreditBalance,
  bandClassCode,
  allowPriorYearInput,
}) => {
  const reconcileSign = isCreditBalance ? -1 : 1;
  // Schedule-level display options parsed once from meta_data JSON.
  // Reusable knobs: show_total (footer Σ row), py_layout (PY render),
  // section_layout (each top-level class renders as its own SECTION —
  // header + line-item rows + per-section Total + per-section Previous
  // Year Total — instead of the PPE "class = one row + note-wide footer"
  // layout; used by the Owners' Capital Account Note's Partners' Capital /
  // Current Account sections).
  const { pyLayout, showTotalRow, sectionLayout } = useMemo<{
    pyLayout: 'inline_columns' | 'separate_grid';
    showTotalRow: boolean;
    sectionLayout: boolean;
  }>(() => {
    if (!scheduleMetaDataJson) return { pyLayout: 'inline_columns', showTotalRow: true, sectionLayout: false };
    try {
      const m = JSON.parse(scheduleMetaDataJson) as {
        display_options?: { py_layout?: string; show_total?: boolean; section_layout?: boolean };
      };
      return {
        pyLayout: m.display_options?.py_layout === 'separate_grid' ? 'separate_grid' : 'inline_columns',
        // Hide the tfoot Σ row when the template explicitly opts out
        // (e.g. ageing matrix where a class-row Grand Total already
        // performs the per-column sum + carries the reconciliation
        // chip — a tfoot Σ row would just duplicate the same numbers).
        showTotalRow: m.display_options?.show_total !== false,
        sectionLayout: m.display_options?.section_layout === true,
      };
    } catch {
      return { pyLayout: 'inline_columns', showTotalRow: true, sectionLayout: false };
    }
  }, [scheduleMetaDataJson]);
  // Live-TB map keyed by class code — used to show the ledger amount as
  // a subline under the is_total / is_prior_year cells, so the auditor
  // sees their entered Net Block against what the TB classification
  // currently sums to. Drift between the two is exactly what the recon
  // chip surfaces at the Note level.
  const liveByClassCode = useMemo(() => {
    const m = new Map<string, { cy: number; py: number }>();
    for (const c of classes) {
      m.set(c.classCode, { cy: c.liveClosingActual, py: c.liveOpeningActual });
    }
    return m;
  }, [classes]);

  const isRounded = displayMode === 'rounded';

  // Per-class "remarks editor visible" toggle. Opened via the class
  // action menu's "Add / Edit Note Text" item. Persists for the modal
  // session; closing the editor by emptying the textarea keeps the row
  // visible until the next modal open.
  const [remarksOpen, setRemarksOpen] = useState<Set<string>>(() => new Set());
  const toggleRemarks = useCallback((classCode: string) => {
    setRemarksOpen(prev => {
      const next = new Set(prev);
      if (next.has(classCode)) next.delete(classCode);
      else next.add(classCode);
      return next;
    });
  }, []);

  // Per-line-item "remarks editor visible" toggle. Keyed by
  // `${classCode}::${localId}` so multiple line items across multiple
  // classes can have remarks editors open simultaneously.
  const [lineItemRemarksOpen, setLineItemRemarksOpen] = useState<Set<string>>(() => new Set());
  const toggleLineItemRemarks = useCallback((classCode: string, localId: string) => {
    const key = `${classCode}::${localId}`;
    setLineItemRemarksOpen(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const setLineItemRemarks = useCallback((classCode: string, localId: string, text: string) => {
    const cd = workspace.class_data?.[classCode];
    if (!cd) return;
    const next = (cd.line_items ?? []).map(li =>
      li.local_id === localId ? { ...li, remarks: text } : li,
    );
    setLineItems(classCode, next);
  }, [workspace.class_data, setLineItems]);


  // Pre-compute a "visible" column list. The render maps over this so
  // grouping columns (is_grouping_field: true) are skipped on the
  // table — they appear elsewhere (sub-head bands etc., when used).
  // For PPE today every column is shown.
  const visibleColumns = useMemo(
    () => columnSpec.filter(c => !c.is_grouping_field),
    [columnSpec],
  );

  const totalColumns = visibleColumns.length + 2; // label + N columns + actions

  // Two-row header support — when any visible column declares a
  // `group`, we emit a top-row banner that spans the contiguous run
  // of columns sharing that group label, and a bottom row carrying
  // each column's own `label`. Columns without a group get a single
  // rowSpan=2 header inline with the banners so the row layout stays
  // aligned. Build the cell sequence here so the render block stays
  // simple.
  type GroupHeaderCell =
    | { kind: 'span'; col: FormatColumnSpec }
    | { kind: 'group'; label: string; colCount: number };
  const hasGroupedHeader = useMemo(
    () => visibleColumns.some(c => !!c.group),
    [visibleColumns],
  );
  const groupHeaderCells = useMemo<GroupHeaderCell[]>(() => {
    if (!hasGroupedHeader) return [];
    const cells: GroupHeaderCell[] = [];
    let i = 0;
    while (i < visibleColumns.length) {
      const c = visibleColumns[i];
      if (!c.group) {
        cells.push({ kind: 'span', col: c });
        i += 1;
        continue;
      }
      const group = c.group;
      let j = i + 1;
      while (j < visibleColumns.length && visibleColumns[j].group === group) j += 1;
      cells.push({ kind: 'group', label: group, colCount: j - i });
      i = j;
    }
    return cells;
  }, [visibleColumns, hasGroupedHeader]);

  // Sticky-right freeze. Walk visibleColumns from the right and bucket
  // any is_total / is_prior_year column as sticky. Stop at the first
  // column that isn't (so we always keep the sticky group contiguous
  // against the actions column).
  //
  // Offsets are MEASURED at runtime (not hardcoded against the colgroup
  // hint widths) — browser column layout can drift from the CSS hint
  // due to padding / box-sizing / borders, leaving a visible gap if
  // we trusted the hint. The actions column is the rightmost reference
  // point; we walk leftward from it, accumulating each sticky column's
  // actual rendered width into the next column's right-offset.
  const stickyCodes = useMemo(() => {
    const codes: string[] = [];
    for (let i = visibleColumns.length - 1; i >= 0; i--) {
      const col = visibleColumns[i];
      if (!col.is_total && !col.is_prior_year) break;
      codes.push(col.code);
    }
    return codes;
  }, [visibleColumns]);

  // tableRef gets attached to the rendered <table> so we can read column
  // widths from the colgroup's <col> elements after layout. Re-measure
  // whenever the column spec or DOM size changes.
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [measuredColWidths, setMeasuredColWidths] = useState<Record<string, number>>({});
  const [measuredActionsWidth, setMeasuredActionsWidth] = useState<number>(COL_ACTIONS_WIDTH);

  // Pick up the actual column widths after each render. Reads the
  // FIRST tbody row's <td> offsetWidth — body rows always carry one
  // flat <td> per column (no rowSpan / colSpan), unlike the two-row
  // <thead> where banner cells span multiple columns and would
  // mis-report widths. ResizeObserver re-fires on viewport changes so
  // the freeze stays correct when the modal resizes.
  //
  // Falls back to the first `<thead><tr>` only when there's no body
  // (empty class list / sub-heading-only render); in that case the
  // single-row header still has one cell per column.
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const expectedCount = visibleColumns.length + 2; // label + N + actions
    const measure = () => {
      const bodyRow = table.querySelector('tbody tr');
      let cells: HTMLElement[] = [];
      if (bodyRow) {
        const tds = bodyRow.querySelectorAll<HTMLElement>('td');
        if (tds.length === expectedCount) cells = Array.from(tds);
      }
      if (cells.length === 0) {
        const headerRow = table.querySelector('thead tr');
        if (!headerRow) return;
        const ths = headerRow.querySelectorAll<HTMLElement>('th');
        if (ths.length === expectedCount) cells = Array.from(ths);
      }
      if (cells.length !== expectedCount) return;

      const widths: Record<string, number> = {};
      for (let i = 0; i < visibleColumns.length; i++) {
        widths[visibleColumns[i].code] = cells[i + 1].offsetWidth;
      }
      const actionsCell = cells[cells.length - 1];
      setMeasuredColWidths(prev => {
        // Skip state update when nothing changed — keeps the layout
        // effect idempotent and avoids loops.
        let same = Object.keys(prev).length === Object.keys(widths).length;
        if (same) for (const k in widths) if (prev[k] !== widths[k]) { same = false; break; }
        return same ? prev : widths;
      });
      setMeasuredActionsWidth(actionsCell.offsetWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(table);
    return () => ro.disconnect();
  }, [visibleColumns]);

  // Build the offset map from the MEASURED widths so the sticky edges
  // line up exactly with the actual column boundaries.
  const stickyRightMap = useMemo(() => {
    type Info = { offset: number; isLeftmost: boolean };
    const map = new Map<string, Info>();
    let offset = measuredActionsWidth;
    for (const code of stickyCodes) {
      map.set(code, { offset, isLeftmost: false });
      offset += measuredColWidths[code] ?? COL_AMOUNT_WIDTH;
    }
    if (stickyCodes.length > 0) {
      const leftmost = stickyCodes[stickyCodes.length - 1];
      const cur = map.get(leftmost)!;
      map.set(leftmost, { ...cur, isLeftmost: true });
    }
    return map;
  }, [stickyCodes, measuredColWidths, measuredActionsWidth]);

  // Build the render row list. Template classes (NoteClassLive) form
  // the tree via parentClassId; engagement-time custom classes
  // (NoteAddedClass) sit at the SAME LEVEL as template classes and
  // interleave by display_order — same merge rule the standard pane
  // and the PDF hydrator follow. Without this merge, "+ Add Custom
  // Class" on a PPE Note would persist but never render.
  const rows = useMemo<RenderRow[]>(() => {
    // Parse meta_data and computation_rule per template class for the
    // ageing-matrix flags + computed-subtotal evaluator.
    interface ClassMeta {
      hideInMainNote: boolean;
      reconcileWithClass?: string;
      reconcileWithTotal: boolean;
      isComputedSubtotal: boolean;
      operands?: string[];
    }
    const parseClassMeta = (c: NoteClassLive): ClassMeta => {
      let meta: { hide_in_main_note?: boolean; reconcile_with_class?: string; reconcile_with_total?: boolean } = {};
      if (c.metaDataJson) {
        try { meta = JSON.parse(c.metaDataJson); } catch { /* ignore */ }
      }
      let isComputedSubtotal = false;
      let operands: string[] | undefined;
      if (c.isComputed && c.computationRuleJson) {
        try {
          const rule = JSON.parse(c.computationRuleJson) as { type?: string; operands?: string[] };
          if (rule.type === 'SUM' && Array.isArray(rule.operands)) {
            isComputedSubtotal = true;
            operands = rule.operands;
          }
        } catch { /* ignore */ }
      }
      return {
        hideInMainNote: meta.hide_in_main_note === true,
        reconcileWithClass: typeof meta.reconcile_with_class === 'string' ? meta.reconcile_with_class : undefined,
        reconcileWithTotal: meta.reconcile_with_total === true,
        isComputedSubtotal,
        operands,
      };
    };

    // Filter out classes flagged hide_in_main_note BEFORE building the
    // parent-tree. They're still classification leaves (TB ledgers map
    // to them); the matrix subtotal rows compare against their saved
    // amount_actual via the reconciliation chip.
    const visibleClasses = classes.filter(c => !parseClassMeta(c).hideInMainNote);

    const byParent = new Map<string | null, NoteClassLive[]>();
    for (const c of visibleClasses) {
      const k = (c.parentClassId ?? null) as string | null;
      const list = byParent.get(k) ?? [];
      list.push(c);
      byParent.set(k, list);
    }
    const out: RenderRow[] = [];

    // Top-level emission: merge template roots + custom classes in
    // display_order. Each emits with depth=0; template subclasses
    // recurse below their parent.
    type TopLevelEntry =
      | { kind: 'tpl';   cls: NoteClassLive;   order: number }
      | { kind: 'added'; cls: NoteAddedClass; order: number };

    const topLevel: TopLevelEntry[] = [];
    for (const c of (byParent.get(null) ?? [])) {
      topLevel.push({ kind: 'tpl', cls: c, order: c.displayOrder });
    }
    for (const a of addedClasses) {
      topLevel.push({ kind: 'added', cls: a, order: a.display_order });
    }
    topLevel.sort((a, b) => a.order - b.order);

    const walkTemplate = (cls: NoteClassLive, depth: number) => {
      const hasChildren = (byParent.get(cls.classId as string) ?? []).length > 0;
      const m = parseClassMeta(cls);
      out.push({
        classCode: cls.classCode,
        label: cls.className,
        depth,
        // isSubHeading stays a TEMPLATE-tree concept (has children) only.
        // Computed-subtotal rows DO render full cells (with computed
        // values) and are flagged separately so the cell editor can
        // lock them and the value injector can fill them.
        isSubHeading: hasChildren,
        isComputedSubtotal: m.isComputedSubtotal,
        computedOperands: m.operands,
        reconcileWithClass: m.reconcileWithClass,
        reconcileWithTotal: m.reconcileWithTotal,
      });
      if (hasChildren) {
        const children = (byParent.get(cls.classId as string) ?? [])
          .slice().sort((a, b) => a.displayOrder - b.displayOrder);
        for (const child of children) walkTemplate(child, depth + 1);
      }
    };

    for (const entry of topLevel) {
      if (entry.kind === 'tpl') {
        walkTemplate(entry.cls, 0);
      } else {
        out.push({
          classCode: entry.cls.local_id,
          label: entry.cls.name,
          depth: 0,
          isSubHeading: false,
        });
      }
    }
    return out;
  }, [classes, addedClasses]);

  // ── Reconciliation context ────────────────────────────────────────────────
  // For ageing-matrix rows flagged reconcile_with_class, the row's Total
  // column compares against `workspace.class_data[targetCode].amount_actual`
  // (the TB-classified ledger total for the hidden classification leaf).
  // For reconcile_with_total, the Grand Total row compares against Σ of
  // hidden classification leaves' amount_actual (the Note's overall
  // classified total).
  const noteClassifiedTotal = useMemo(() => {
    // Sum amount_actual across hidden classification leaves — every class
    // that's flagged hide_in_main_note via meta_data.
    let s = 0;
    for (const c of classes) {
      let hide = false;
      if (c.metaDataJson) {
        try {
          const m = JSON.parse(c.metaDataJson) as { hide_in_main_note?: boolean };
          hide = m.hide_in_main_note === true;
        } catch { /* ignore */ }
      }
      if (!hide) continue;
      const a = workspace.class_data?.[c.classCode]?.amount_actual;
      if (typeof a === 'number') s += a;
    }
    return s;
  }, [classes, workspace.class_data]);

  // Tolerance for the reconciliation drift chip — reuse the engagement-level
  // rounding tolerance (typically Rs 1.00). Mirrors the existing per-class
  // ledger-drift threshold used on other Notes.
  const reconcileTolerance = 1.0;

  // ── Ageing-matrix computed subtotals ──────────────────────────────────────
  // For rows flagged isComputedSubtotal (computation_rule type=SUM with named
  // operand classes), build a synthetic column_values dict that's Σ of the
  // operand classes' column_values per column. Two-pass: first sum operands,
  // FSA-2.2 — column lookup for the evaluator's is_deduction flag.
  // Columns marked is_deduction get their value negated when resolved
  // as a formula operand. Lets PPE-style specs use '+' uniformly.
  // Declared up here so every evaluator call site below (subtotal
  // synth, recomputeRow, handlePyCellChange) can reference it.
  const colByCodeForEvaluator = useMemo(() => {
    const m: Record<string, FormatColumnSpec> = {};
    for (const c of visibleColumns) m[c.code] = c;
    return m;
  }, [visibleColumns]);

  // then evaluate any is_computed columns (e.g. Total = sum-of-buckets)
  // against the freshly-summed row so subtotal Total cells reflect their
  // own row. The synth dict feeds two paths: (a) the render override via
  // effectiveWorkspace, (b) a persistence useEffect below that writes
  // it back into the canonical workspace.class_data so saved state +
  // PDF reads see the same numbers.
  const subtotalColumnValues = useMemo<Map<string, Record<string, number>>>(() => {
    const computedRows = rows.filter(r => r.isComputedSubtotal && r.computedOperands && r.computedOperands.length > 0);
    if (computedRows.length === 0) return new Map();
    const amountColumns = visibleColumns.filter(c => c.data_type === 'amount');
    const inputAmountColumns = amountColumns.filter(c => !c.is_computed);
    const computedAmountColumns = amountColumns.filter(c => c.is_computed && c.formula);

    // Resolve computed-row column_values in a topological-ish pass:
    // computed rows may themselves reference other computed rows
    // (Grand Total = Subtotal MSME + Subtotal Others). Iterate fixed-
    // point style up to N iterations until no change.
    const synthValues = new Map<string, Record<string, number>>();
    // Dual-mode operand readers: getRowVal reads the base (actuals)
    // key; getRowValRounded reads the __rounded companion. The synth
    // sums BOTH so subtotals work whether the auditor typed in actuals
    // mode (base populated, rounded derived via autoRound) OR in
    // rounded mode (only __rounded populated). Without the dual read,
    // rounded-mode-only inputs surface as 0 in subtotals (the original
    // bug reported on the PY ageing grid; same bug latent on CY).
    const getRowVal = (code: string, colCode: string): number => {
      if (synthValues.has(code)) {
        const v = synthValues.get(code)![colCode];
        return typeof v === 'number' ? v : 0;
      }
      const cv = workspace.class_data?.[code]?.column_values;
      if (cv && typeof cv[colCode] === 'number') return cv[colCode];
      return 0;
    };
    const getRowValRounded = (code: string, colCode: string): number => {
      const k = colCode + ROUNDED_SUFFIX;
      if (synthValues.has(code)) {
        const v = synthValues.get(code)![k];
        return typeof v === 'number' ? v : 0;
      }
      const cv = workspace.class_data?.[code]?.column_values;
      if (cv && typeof cv[k] === 'number') return cv[k];
      return 0;
    };

    let changed = true;
    let iter = 0;
    while (changed && iter < 10) {
      changed = false;
      iter += 1;
      for (const row of computedRows) {
        const next: Record<string, number> = {};
        // Sum input columns from operands — both base and __rounded.
        for (const col of inputAmountColumns) {
          let sumBase = 0;
          let sumRounded = 0;
          for (const op of row.computedOperands ?? []) {
            sumBase    += getRowVal(op, col.code);
            sumRounded += getRowValRounded(op, col.code);
          }
          const baseV = Math.round(sumBase * 100) / 100;
          const roundedV = Math.round(sumRounded * 100) / 100;
          next[col.code] = baseV;
          // Prefer operands' summed __rounded; fall back to autoRound
          // of the actuals sum when operands had no rounded companion.
          next[col.code + ROUNDED_SUFFIX] = roundedV !== 0 ? roundedV : autoRound(baseV, roundingFactorKey);
        }
        // Computed columns: dual-eval against both operand views.
        const roundedOperandsView: Record<string, number> = {};
        for (const c of inputAmountColumns) {
          const r = next[c.code + ROUNDED_SUFFIX];
          if (typeof r === 'number' && Number.isFinite(r)) roundedOperandsView[c.code] = r;
        }
        for (const col of computedAmountColumns) {
          try {
            const baseResult    = evaluateFormula(col.formula as string, next, colByCodeForEvaluator);
            const roundedResult = evaluateFormula(col.formula as string, roundedOperandsView, colByCodeForEvaluator);
            const v = typeof baseResult === 'number' ? Math.round(baseResult * 100) / 100 : 0;
            next[col.code] = v;
            const fromActual = autoRound(v, roundingFactorKey);
            next[col.code + ROUNDED_SUFFIX] = fromActual !== 0
              ? fromActual
              : Math.round((roundedResult ?? 0) * 100) / 100;
          } catch { /* ignore */ }
        }
        const prev = synthValues.get(row.classCode);
        // Compare both base and __rounded keys for change detection so
        // rounded-only changes trigger a re-write.
        const hasChange = !prev
          || amountColumns.some(c => prev[c.code] !== next[c.code])
          || amountColumns.some(c => prev[c.code + ROUNDED_SUFFIX] !== next[c.code + ROUNDED_SUFFIX]);
        if (hasChange) {
          synthValues.set(row.classCode, next);
          changed = true;
        }
      }
    }

    return synthValues;
  }, [workspace, rows, visibleColumns, roundingFactorKey, colByCodeForEvaluator]);

  // Render-time override: inject the synthetic column_values onto a
  // shallow-cloned workspace so downstream cell renderers read them as
  // if they were saved. Pairs with the persistence useEffect below
  // that writes the same values into the canonical workspace state.
  const effectiveWorkspace = useMemo<NoteWorkspaceData>(() => {
    if (subtotalColumnValues.size === 0) return workspace;
    const nextClassData: NoteWorkspaceData['class_data'] = { ...workspace.class_data };
    for (const [code, cv] of subtotalColumnValues) {
      const existing = nextClassData[code] ?? ({} as NoteClassData);
      nextClassData[code] = { ...existing, column_values: cv } as NoteClassData;
    }
    return { ...workspace, class_data: nextClassData };
  }, [workspace, subtotalColumnValues]);

  // Persistence: write computed subtotal column_values into the
  // canonical workspace.class_data so the save flow + PDF render path
  // both see the same values.
  //
  // IMPORTANT — only column_values is written; amount_actual stays 0.
  // The matrix subtotal rows are presentation-only (no ledgers map to
  // them, no contribution to the Note total). The Note total is the
  // Σ of the TB-classified leaves' amount_actual (N7_MSME / N7_OTHERS
  // for Trade Payables), which feeds both the Total CY display and the
  // canSave reconciliation. If we wrote amount_actual on subtotal rows,
  // it would double-count: leaf -60M + synth subtotal +60M + synth
  // grand total +60M ⇒ canSave drift = 120M, Save button never
  // disables. PDF + on-screen totals read from column_values, which
  // we DO write.
  useEffect(() => {
    if (subtotalColumnValues.size === 0) return;
    for (const [code, cv] of subtotalColumnValues) {
      const existing = workspace.class_data?.[code];
      const existingCv = existing?.column_values ?? null;
      // Compare only the keys the synth dict writes (input + computed
      // columns and their __rounded companions). Don't traverse extra
      // keys in existingCv — those came from somewhere else (line-item
      // rollup, prior save shape) and aren't ours to mutate.
      let same = existingCv != null;
      if (same && existingCv) {
        for (const k of Object.keys(cv)) {
          if (existingCv[k] !== cv[k]) { same = false; break; }
        }
      }
      if (same) continue;
      // Merge synth keys over existingCv so we don't clobber unrelated
      // keys saved earlier.
      const nextCv = { ...(existingCv ?? {}), ...cv };
      setClassField(code, 'column_values', nextCv);
    }
  }, [subtotalColumnValues, workspace.class_data, setClassField]);

  // ── PY Total recompute ────────────────────────────────────────────────────
  // PPE-style Notes (and any other multi-column schedule that declares
  // formula / drift_formula columns) auto-derive 4 of the 10 PY Total
  // cells when the auditor enters movement inputs:
  //   • is_computed + formula  → evaluated like the class row's Phase B
  //                              (e.g. gross_closing = opening + add − del)
  //   • drift_formula present  → promoted to a compute formula in THIS
  //                              row only (e.g. net_closing = gross_closing
  //                              − dep_closing). On class rows drift_formula
  //                              stays a validation chip against a source
  //                              column (line items / live TB); the PY row
  //                              has no source other than auditor entry, so
  //                              the same expression doubles as the value.
  //
  // Dual-mode editing mirrors the class-row pass: actuals operand → autoRound
  // gives the __rounded companion; rounded operand → second formula eval
  // produces the rounded companion when actuals are stale/zero.
  const handlePyCellChange = useCallback((changedCode: string, value: number) => {
    if (readOnly) return;
    const current = workspace.prior_year_totals ?? {};
    const next: Record<string, number> = { ...current };
    next[changedCode] = value;

    const isRoundedEdit = changedCode.endsWith(ROUNDED_SUFFIX);

    // Phase A — refresh __rounded companions for non-computed amount
    // columns when the edit was on the actual key. Skip computed and
    // drift-bearing columns (Phase B owns those).
    for (const col of visibleColumns) {
      if (col.data_type !== 'amount') continue;
      if (col.is_computed) continue;
      if (col.drift_formula) continue;
      const a = next[col.code];
      if (typeof a !== 'number') continue;
      const rk = col.code + ROUNDED_SUFFIX;
      if (changedCode !== rk) {
        next[rk] = autoRound(a, roundingFactorKey);
      }
    }

    // Build a rounded-operands view so the formula evaluator can resolve
    // operand codes against their __rounded companions when the auditor
    // typed only in rounded mode and actuals stayed undefined.
    const roundedOperands: Record<string, number> = {};
    for (const col of visibleColumns) {
      if (col.data_type !== 'amount') continue;
      const r = next[col.code + ROUNDED_SUFFIX];
      if (typeof r === 'number' && Number.isFinite(r)) {
        roundedOperands[col.code] = r;
      }
    }

    // Phase B — evaluate computed columns. For columns with explicit
    // is_computed + formula, use the formula. For columns whose only
    // expression is drift_formula (net_closing, net_py), use that. Same
    // dual-write rule for the __rounded companion as the class-row pass.
    for (const col of visibleColumns) {
      if (col.data_type !== 'amount') continue;
      const expr = (col.is_computed && col.formula) ? col.formula : col.drift_formula;
      if (!expr) continue;
      const v = evaluateFormula(expr, next, colByCodeForEvaluator);
      next[col.code] = v;
      const fromActual = autoRound(v, roundingFactorKey);
      const fromRounded = evaluateFormula(expr, roundedOperands, colByCodeForEvaluator);
      next[col.code + ROUNDED_SUFFIX] = fromActual !== 0 ? fromActual : fromRounded;
    }

    // Diff against current; write only changed keys. Preserve the existing
    // "0 ⇒ null" sparse-storage idiom from the previous handler.
    const allKeys = new Set([...Object.keys(current), ...Object.keys(next)]);
    for (const k of allKeys) {
      const a = current[k];
      const b = next[k];
      if (a === b) continue;
      const sparse = (b === undefined || b === 0) ? null : b;
      setPriorYearTotal(k, sparse);
    }
  }, [workspace.prior_year_totals, visibleColumns, roundingFactorKey, setPriorYearTotal, readOnly, colByCodeForEvaluator]);

  /** TRUE when this column's value is derived by the PY Total recompute pass. */
  const isPyComputedColumn = useCallback((c: FormatColumnSpec): boolean =>
    !!((c.is_computed && c.formula) || c.drift_formula),
  []);

  // Per-SECTION Previous Year Total edit (section_layout notes). Same two-phase
  // recompute as the note-wide handlePyCellChange, but the comparative is
  // stored per class in class_data[classCode].prior_year_totals rather than
  // the workspace-level prior_year_totals.
  const handleClassPyCellChange = useCallback((classCode: string, changedCode: string, value: number) => {
    if (readOnly) return;
    const cd = workspace.class_data?.[classCode];
    const current = cd?.prior_year_totals ?? {};
    const next: Record<string, number> = { ...current };
    next[changedCode] = value;

    // Phase A — refresh __rounded companions for non-computed amount columns.
    for (const col of visibleColumns) {
      if (col.data_type !== 'amount') continue;
      if (col.is_computed) continue;
      if (col.drift_formula) continue;
      const a = next[col.code];
      if (typeof a !== 'number') continue;
      const rk = col.code + ROUNDED_SUFFIX;
      if (changedCode !== rk) next[rk] = autoRound(a, roundingFactorKey);
    }

    const roundedOperands: Record<string, number> = {};
    for (const col of visibleColumns) {
      if (col.data_type !== 'amount') continue;
      const r = next[col.code + ROUNDED_SUFFIX];
      if (typeof r === 'number' && Number.isFinite(r)) roundedOperands[col.code] = r;
    }

    // Phase B — computed columns (is_computed+formula, else drift_formula).
    for (const col of visibleColumns) {
      if (col.data_type !== 'amount') continue;
      const expr = (col.is_computed && col.formula) ? col.formula : col.drift_formula;
      if (!expr) continue;
      const v = evaluateFormula(expr, next, colByCodeForEvaluator);
      next[col.code] = v;
      const fromActual = autoRound(v, roundingFactorKey);
      const fromRounded = evaluateFormula(expr, roundedOperands, colByCodeForEvaluator);
      next[col.code + ROUNDED_SUFFIX] = fromActual !== 0 ? fromActual : fromRounded;
    }

    // Store sparse — drop zero / non-finite so an untouched PY row stays empty.
    const cleaned: Record<string, number> = {};
    for (const [k, v] of Object.entries(next)) {
      if (typeof v === 'number' && Number.isFinite(v) && v !== 0) cleaned[k] = v;
    }
    setClassField(classCode, 'prior_year_totals', cleaned);
  }, [workspace.class_data, visibleColumns, roundingFactorKey, colByCodeForEvaluator, setClassField, readOnly]);

  // Inline editor state — at most one line item is in "editing" state
  // at a time. Mirrors the standard pane's single-instance editor:
  // localId-null = "add"; localId-set = "edit existing".
  type Editing =
    | { classCode: string; localId: null; draft: LineItemDraft }
    | { classCode: string; localId: string; draft: LineItemDraft };
  const [editing, setEditing] = useState<Editing | null>(null);

  const handleStartAddLineItem = useCallback((classCode: string) => {
    setEditing({ classCode, localId: null, draft: blankLineItemDraft() });
  }, []);

  const handleStartEditLineItem = useCallback((classCode: string, li: NoteLineItem) => {
    setEditing({
      classCode,
      localId: li.local_id,
      draft: {
        name: li.name,
        allocation_mode: li.allocation_mode,
        ledger_ids: (li.ledgers ?? []).map(l => String(l.ledger_id)),
        manual_amount_actual: li.amount_actual,
        manual_prior_amount_actual: li.prior_amount_actual,
        dimension_values: { ...(li.dimension_values ?? {}) },
        cross_note_amount: li.cross_note_amount ?? 0,
        prior_cross_note_amount: li.prior_cross_note_amount ?? 0,
        cross_note_target: li.cross_note_target ?? null,
      },
    });
  }, []);

  const handleEditorCancel = useCallback(() => setEditing(null), []);

  // Recompute one row's column_values (write a single input, then
  // refresh every rounded companion + computed cell using the new
  // value set). Pure — takes the current dict + the changed slot,
  // returns the new dict.
  //
  // INVARIANT: A rounded-mode edit (changedCode ending in __rounded)
  // MUST NEVER mutate the actual counterpart (the key without the
  // suffix). The user's intent in rounded mode is "override the
  // displayed rounded figure" — the canonical actual stays as the
  // editor-sourced amount (Ledger sum / Manual entry / typed input).
  const recomputeRow = useCallback((
    current: Record<string, number> | null | undefined,
    changedCode: string,
    changedValue: number,
  ): Record<string, number> => {
    const next: Record<string, number> = { ...(current ?? {}) };
    next[changedCode] = changedValue;

    const isRoundedEdit = changedCode.endsWith(ROUNDED_SUFFIX);
    const editedActualCode = isRoundedEdit
      ? changedCode.slice(0, -ROUNDED_SUFFIX.length)
      : null;

    // Snapshot sourced (is_total / is_prior_year) keys from `current`
    // so we can restore them at the end. These columns are SOURCED
    // (Manual amount / Σ Ledgers via editor / Update from Ledger) and
    // must NEVER drift as a side-effect of an unrelated inline edit on
    // a movement column. Snapshot captures undefined for absent keys —
    // we delete those from `next` at restore time so we don't introduce
    // phantom 0s on rows that never had a sourced Net Block.
    const sourcedKeys = new Set<string>();
    for (const col of visibleColumns) {
      // Sourced = "value comes from outside the formula pipeline" with no
      // expression to derive it from. A column qualifies as sourced ONLY
      // when it has neither is_computed+formula nor drift_formula. PPE's
      // Net Block CY/PY are NOT sourced anymore — they now derive from
      // drift_formula on every edit, same as is_computed columns. Schedules
      // with truly sourced totals (no formula expression at all) still get
      // the snapshot/restore protection.
      const isFormulaDriven = (col.is_computed && col.formula) || col.drift_formula;
      if ((col.is_total || col.is_prior_year) && !isFormulaDriven) {
        sourcedKeys.add(col.code);
        sourcedKeys.add(col.code + ROUNDED_SUFFIX);
      }
    }
    const sourcedSnapshot = new Map<string, number | undefined>();
    for (const key of sourcedKeys) {
      sourcedSnapshot.set(key, (current ?? {})[key]);
    }

    // Phase A: refresh __rounded companions for non-computed amount
    // columns. Override happens by editing the __rounded code directly;
    // we skip that case so the auditor's override isn't blown away.
    for (const col of visibleColumns) {
      if (col.data_type !== 'amount') continue;
      if (col.is_computed) continue;
      const actual = next[col.code];
      if (typeof actual !== 'number') continue;
      const roundedKey = col.code + ROUNDED_SUFFIX;
      if (changedCode !== roundedKey) {
        next[roundedKey] = autoRound(actual, roundingFactorKey);
      }
    }

    // Phase B: re-evaluate every computed column in declared order
    // (columnSpec ordering guarantees dependencies precede dependants).
    //
    // GUARD: when the auditor is editing a COMPUTED column's __rounded
    // companion, skip that column entirely — preserving BOTH the
    // existing actual AND the typed rounded override. Re-evaluating
    // the formula with missing operands would otherwise zero out the
    // actual (evaluateFormula returns 0 for all-undefined operands)
    // and the rounded write would clobber the auditor's override. The
    // intent of editing __rounded directly is "treat my number as
    // canonical for this row" — Phase B re-derivation fights that.
    //
    // Rounded-mode operand edits (e.g. ageing matrix bucket cells
    // typed in THOUSANDS view) only populate the __rounded companion.
    // Build a parallel rounded-operand map so the formula evaluator
    // can resolve `lt_1y` against `next.lt_1y__rounded` and the
    // resulting total__rounded reflects the auditor's rounded inputs
    // even when actuals are stale or zero.
    const roundedOperands: Record<string, number> = {};
    for (const col of visibleColumns) {
      if (col.data_type !== 'amount') continue;
      const r = next[col.code + ROUNDED_SUFFIX];
      if (typeof r === 'number' && Number.isFinite(r)) {
        roundedOperands[col.code] = r;
      }
    }
    for (const col of visibleColumns) {
      // Resolve the formula expression for this column:
      //  • is_computed + formula  → use formula (e.g. gross_closing)
      //  • drift_formula present  → use drift_formula AS the compute formula
      //                              (e.g. net_closing = gross_closing − dep_closing)
      // On class + line rows the drift_formula was previously a validation
      // chip against a sourced value; with strict-formula Net Block, it
      // doubles as the compute expression. The drift chip then compares
      // this formula result against the live TB / manual / cross-note
      // source — chip's role is preserved, just with sides swapped.
      const expr = (col.is_computed && col.formula) ? col.formula : col.drift_formula;
      if (!expr) continue;
      if (editedActualCode === col.code) continue;
      const v = evaluateFormula(expr, next, colByCodeForEvaluator);
      next[col.code] = v;
      if (col.data_type === 'amount') {
        // Two paths for the __rounded companion:
        //  (a) auto-round the freshly-computed actual (works in actuals-mode edits);
        //  (b) evaluate the formula against the rounded-operands map (works in
        //      rounded-mode edits where actuals stayed undefined).
        // Take the non-zero one — both give the same answer when both succeed,
        // and (a)=0 + (b)≠0 is the rounded-edit case where only (b) is real.
        const fromActual = autoRound(v, roundingFactorKey);
        const fromRounded = evaluateFormula(expr, roundedOperands, colByCodeForEvaluator);
        next[col.code + ROUNDED_SUFFIX] = fromActual !== 0 ? fromActual : fromRounded;
      }
    }

    // Final restore — sourced keys (Net Block CY / PY + __rounded
    // companions) snap back to their pre-Phase snapshot, EXCEPT for
    // the key the auditor edited directly. This structurally
    // guarantees the invariant that movement edits (gross_*, dep_*)
    // never touch the sourced Net Block, even if a future Phase A/B
    // change tries to.
    //
    // EXCEPTION: when the auditor edits a sourced BASE column directly
    // (e.g. an editable Opening / is_prior_year cell typed in Actuals
    // view), keep the freshly auto-rounded companion Phase A produced
    // for it too — otherwise the __rounded value is restored to the
    // stale pre-edit snapshot and the Rounded view shows a blank/old
    // figure for the value the auditor just entered.
    const editedRoundedCompanion = isRoundedEdit ? null : changedCode + ROUNDED_SUFFIX;
    for (const key of sourcedKeys) {
      if (key === changedCode) continue;
      if (key === editedRoundedCompanion) continue;
      const snap = sourcedSnapshot.get(key);
      if (snap === undefined) {
        delete next[key];
      } else {
        next[key] = snap;
      }
    }

    return next;
  }, [visibleColumns, roundingFactorKey, colByCodeForEvaluator]);

  // Sum line-item column_values into the class's column_values for
  // every input + computed column. Used when line items exist — the
  // class row becomes a read-only rollup of its line items (Option A
  // invariant). Returns the rolled-up dict; caller writes it back.
  const rollupFromLineItems = useCallback((
    lineItems: NoteLineItem[],
  ): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const col of visibleColumns) {
      if (col.data_type !== 'amount') continue;
      let sumActual = 0;
      let sumRounded = 0;
      for (const li of lineItems) {
        const cv = li.column_values ?? {};
        const a = cv[col.code];
        const r = cv[col.code + ROUNDED_SUFFIX];
        if (typeof a === 'number') sumActual += a;
        if (typeof r === 'number') sumRounded += r;
      }
      out[col.code] = sumActual;
      out[col.code + ROUNDED_SUFFIX] = sumRounded;
    }
    return out;
  }, [visibleColumns]);

  // Per-class write: route through recomputeRow + Option A rollup. When
  // the class has line items, the class row's inputs are read-only and
  // edits flow into the line items instead; this handler only fires for
  // classes with no line items today.
  const handleCellChange = useCallback((classCode: string, colCode: string, value: number) => {
    const cd = workspace.class_data?.[classCode];
    const next = recomputeRow(cd?.column_values, colCode, value);
    setClassField(classCode, 'column_values', next);
  }, [workspace.class_data, recomputeRow, setClassField]);

  // Line-item edit: update one cell on one line item, then re-roll up
  // the class's column_values so the class row mirrors Σ line items.
  //
  // When the edit is on a Net Block CY/PY cell (is_total / is_prior_year),
  // we ALSO sync the legacy `li.amount_actual` / `li.amount_rounded` /
  // `prior_*` fields so the line item's classic-shape rollup (consumed by
  // setLineItems → cd.amount_actual / cd.amount_rounded) stays consistent
  // with the multi-col column_values. Without this sync, a rounded edit on
  // Net Block updates column_values[net_closing__rounded] but leaves
  // li.amount_rounded stale, which causes the class-level legacy
  // amount_rounded total to drift from the multi-col rollup at save time
  // and to flash blank when any downstream consumer reads the legacy
  // field instead of column_values.
  const handleLineItemCellChange = useCallback((
    classCode: string,
    localId: string,
    colCode: string,
    value: number,
  ) => {
    const cd = workspace.class_data?.[classCode];
    if (!cd) return;
    const totalCode = visibleColumns.find(c => c.is_total)?.code ?? null;
    const priorCode = visibleColumns.find(c => c.is_prior_year)?.code ?? null;
    const nextLineItems = (cd.line_items ?? []).map(li => {
      if (li.local_id !== localId) return li;
      const nextColumnValues = recomputeRow(li.column_values, colCode, value);
      // Mirror Net Block column_values into the legacy fields so any
      // legacy-path consumer (class-level cd.amount_rounded rollup,
      // backend sync, etc.) sees the same numbers as the multi-col path.
      let amount_actual = li.amount_actual;
      let amount_rounded = li.amount_rounded;
      let prior_amount_actual = li.prior_amount_actual;
      let prior_amount_rounded = li.prior_amount_rounded;
      if (totalCode) {
        const a = nextColumnValues[totalCode];
        const r = nextColumnValues[totalCode + ROUNDED_SUFFIX];
        if (typeof a === 'number') amount_actual = a;
        if (typeof r === 'number') amount_rounded = r;
      }
      if (priorCode) {
        const a = nextColumnValues[priorCode];
        const r = nextColumnValues[priorCode + ROUNDED_SUFFIX];
        if (typeof a === 'number') prior_amount_actual = a;
        if (typeof r === 'number') prior_amount_rounded = r;
      }
      return {
        ...li,
        column_values: nextColumnValues,
        amount_actual,
        amount_rounded,
        prior_amount_actual,
        prior_amount_rounded,
      };
    });
    setLineItems(classCode, nextLineItems);
    // Class rollup: Σ line items → class column_values. The save
    // service also enforces this server-side as a safety net.
    const rolled = rollupFromLineItems(nextLineItems);
    setClassField(classCode, 'column_values', rolled);
  }, [workspace.class_data, visibleColumns, recomputeRow, rollupFromLineItems, setLineItems, setClassField]);

  // Line-item rename — pure label change, no rollup needed.
  const handleLineItemRename = useCallback((classCode: string, localId: string, name: string) => {
    const cd = workspace.class_data?.[classCode];
    if (!cd) return;
    const next = (cd.line_items ?? []).map(li =>
      li.local_id === localId ? { ...li, name } : li,
    );
    setLineItems(classCode, next);
  }, [workspace.class_data, setLineItems]);

  // Append a blank line item to a class. Initial column_values is an
  // empty dict — auditor fills in via the row's input cells.
  const handleAddLineItem = useCallback((classCode: string) => {
    const cd = workspace.class_data?.[classCode];
    const existing = cd?.line_items ?? [];
    const nextOrder = (existing[existing.length - 1]?.display_order ?? 0) + 1;
    const newLi: NoteLineItem = {
      local_id: makeLocalId(),
      name: '',
      display_order: nextOrder,
      allocation_mode: 'manual',
      ledgers: [],
      manual_amount_actual: 0,
      amount_actual: 0,
      amount_rounded_auto: 0,
      amount_rounded: 0,
      prior_amount_actual: 0,
      prior_amount_rounded_auto: 0,
      prior_amount_rounded: 0,
      column_values: {},
    };
    setLineItems(classCode, [...existing, newLi]);
  }, [workspace.class_data, setLineItems]);

  // Remove a line item. After removal, re-roll the class column_values
  // so the displayed total updates immediately.
  const handleDeleteLineItem = useCallback((classCode: string, localId: string) => {
    const cd = workspace.class_data?.[classCode];
    if (!cd) return;
    const next = (cd.line_items ?? []).filter(li => li.local_id !== localId);
    setLineItems(classCode, next);
    // If no line items remain, the class amount stays at whatever was
    // last in column_values — leave it; auditor will edit the class row
    // directly. Otherwise re-roll up.
    if (next.length > 0) {
      setClassField(classCode, 'column_values', rollupFromLineItems(next));
    }
  }, [workspace.class_data, rollupFromLineItems, setLineItems, setClassField]);

  // Switch a line item's allocation_mode. Mirrors the standard pane's
  // Manual / Ledgers source selector. Clears ledger selection when
  // moving to Manual; leaves column_values intact (auditor's typed
  // movement detail survives either way).
  const handleLineItemSourceChange = useCallback((
    classCode: string,
    localId: string,
    mode: 'manual' | 'ledgers',
  ) => {
    const cd = workspace.class_data?.[classCode];
    if (!cd) return;
    const next = (cd.line_items ?? []).map(li => {
      if (li.local_id !== localId) return li;
      const updated: NoteLineItem = {
        ...li,
        allocation_mode: mode,
        ledgers: mode === 'ledgers' ? (li.ledgers ?? []) : [],
      };
      return updated;
    });
    setLineItems(classCode, next);
  }, [workspace.class_data, setLineItems]);

  // Ledger selection change for a line item in 'ledgers' mode. Snapshots
  // the selected ledgers into li.ledgers and writes the running sums
  // into the line's column_values[is_total / is_prior_year]. Other
  // movement columns are auditor-entered and unchanged here.
  // Re-rolls up the class total afterward (Option A invariant).
  const handleLineItemLedgersChange = useCallback((
    classCode: string,
    localId: string,
    selectedLedgerIds: Set<string>,
  ) => {
    const liveTemplate = classes.find(c => c.classCode === classCode);
    const liveLedgers = liveTemplate?.ledgers ?? [];
    const liveById = new Map(liveLedgers.map(l => [String(l.ledgerId), l]));

    // Resolve which is_total / is_prior_year column codes to write into.
    const totalCode    = visibleColumns.find(c => c.is_total)?.code ?? null;
    const priorCode    = visibleColumns.find(c => c.is_prior_year)?.code ?? null;

    const cd = workspace.class_data?.[classCode];
    if (!cd) return;
    const next = (cd.line_items ?? []).map(li => {
      if (li.local_id !== localId) return li;
      const nextLedgers = Array.from(selectedLedgerIds).flatMap((id, i) => {
        const live = liveById.get(id);
        if (!live) return [];
        return [{
          ledger_id: live.ledgerId,
          ledger_name: live.ledgerName,
          amount_actual: live.closingActual,
          prior_amount_actual: live.openingActual,
          display_order: i + 1,
        }];
      });
      const sumCy = nextLedgers.reduce((s, l) => s + l.amount_actual, 0);
      const sumPy = nextLedgers.reduce((s, l) => s + l.prior_amount_actual, 0);

      const nextColumnValues: Record<string, number> = { ...(li.column_values ?? {}) };
      if (totalCode) {
        nextColumnValues[totalCode] = sumCy;
        nextColumnValues[totalCode + ROUNDED_SUFFIX] = autoRound(sumCy, roundingFactorKey);
      }
      if (priorCode) {
        nextColumnValues[priorCode] = sumPy;
        nextColumnValues[priorCode + ROUNDED_SUFFIX] = autoRound(sumPy, roundingFactorKey);
      }

      return {
        ...li,
        allocation_mode: 'ledgers' as const,
        ledgers: nextLedgers,
        column_values: nextColumnValues,
        amount_actual: sumCy,
        prior_amount_actual: sumPy,
      };
    });
    setLineItems(classCode, next);
    setClassField(classCode, 'column_values', rollupFromLineItems(next));
  }, [classes, visibleColumns, workspace.class_data, roundingFactorKey, rollupFromLineItems, setLineItems, setClassField]);

  // Save the shared LineItemEditor's draft. The editor owns name +
  // source + Net Block CY/PY (via Manual amount OR Σ Ledgers). Movement
  // columns are NOT entered in the editor — they're auditor-entered
  // inline on the row after save. So we persist the line item with
  // just the Net Block columns populated and leave the rest of
  // column_values empty for the inline flow to fill in.
  const handleEditorSave = useCallback((draft: LineItemDraft) => {
    if (!editing) return;
    const { classCode, localId } = editing;
    const cd = workspace.class_data?.[classCode];
    if (!cd) return;
    const liveTemplate = classes.find(c => c.classCode === classCode);
    const liveLedgersForClass = liveTemplate?.ledgers ?? [];
    const liveById = new Map(liveLedgersForClass.map(l => [String(l.ledgerId), l]));

    const totalCode = visibleColumns.find(c => c.is_total)?.code ?? null;
    const priorCode = visibleColumns.find(c => c.is_prior_year)?.code ?? null;
    const targetLocalId = localId ?? (typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : 'li_' + Math.random().toString(36).slice(2));

    let cyAmount = 0;
    let pyAmount = 0;
    let ledgersForLi: NoteLineItem['ledgers'] = [];
    if (draft.allocation_mode === 'ledgers') {
      const picked = draft.ledger_ids
        .map(id => liveById.get(id))
        .filter((l): l is NoteLedger => !!l);
      cyAmount = picked.reduce((s, l) => s + l.closingActual, 0);
      pyAmount = picked.reduce((s, l) => s + l.openingActual, 0);
      ledgersForLi = picked.map((l, i) => ({
        ledger_id: l.ledgerId,
        ledger_name: l.ledgerName,
        amount_actual: l.closingActual,
        prior_amount_actual: l.openingActual,
        display_order: i + 1,
        remarks: null,
      }));
    } else {
      cyAmount = draft.manual_amount_actual;
      pyAmount = draft.manual_prior_amount_actual;
    }

    // Preserve existing column_values (movement detail) if editing —
    // the editor doesn't touch movement; only Net Block CY/PY are
    // written here.
    const existingLi = (cd.line_items ?? []).find(li => li.local_id === localId);
    const nextColumnValues: Record<string, number> = { ...(existingLi?.column_values ?? {}) };
    if (totalCode) {
      nextColumnValues[totalCode] = cyAmount;
      nextColumnValues[totalCode + ROUNDED_SUFFIX] = autoRound(cyAmount, roundingFactorKey);
    }
    if (priorCode) {
      nextColumnValues[priorCode] = pyAmount;
      nextColumnValues[priorCode + ROUNDED_SUFFIX] = autoRound(pyAmount, roundingFactorKey);
    }

    const cyAuto = autoRound(cyAmount, roundingFactorKey);
    const pyAuto = autoRound(pyAmount, roundingFactorKey);

    const built: NoteLineItem = {
      local_id: targetLocalId,
      name: draft.name.trim(),
      display_order: 0,
      allocation_mode: draft.allocation_mode === 'ledgers' ? 'ledgers' : 'manual',
      ledgers: ledgersForLi,
      manual_amount_actual: draft.allocation_mode === 'manual' ? draft.manual_amount_actual : null,
      amount_actual: cyAmount,
      amount_rounded_auto: cyAuto,
      amount_rounded: cyAuto,
      prior_amount_actual: pyAmount,
      prior_amount_rounded_auto: pyAuto,
      prior_amount_rounded: pyAuto,
      remarks: null,
      dimension_values: { ...draft.dimension_values },
      cross_note_amount: draft.cross_note_amount !== 0 ? draft.cross_note_amount : null,
      prior_cross_note_amount: draft.prior_cross_note_amount !== 0 ? draft.prior_cross_note_amount : null,
      cross_note_target: draft.cross_note_amount !== 0 ? draft.cross_note_target : null,
      column_values: nextColumnValues,
    };

    const existing = cd.line_items ?? [];
    const updated = localId
      ? existing.map(li => (li.local_id === localId ? built : li))
      : [...existing, built];
    const renumbered = updated.map((li, i) => ({ ...li, display_order: (i + 1) * 10 }));
    setLineItems(classCode, renumbered);
    setClassField(classCode, 'column_values', rollupFromLineItems(renumbered));
    setEditing(null);
  }, [
    editing, workspace.class_data, classes, visibleColumns,
    roundingFactorKey, rollupFromLineItems, setLineItems, setClassField,
  ]);

  // Footer totals — sum every amount column across visible (non-sub-
  // heading) class rows. Built as a single dict with BOTH the actual
  // and __rounded entries per column so it can be:
  //   (a) read by the live footer render below (actual / rounded mode
  //       depending on display toggle), and
  //   (b) persisted verbatim to workspace.column_totals so the PDF
  //       renderer reads it without recomputing.
  //
  // Hidden classes (class_data[code].hide_in_print = TRUE) are
  // excluded — the PDF skips them too, so the persisted Total must
  // match the PDF presentation. Sub-headings (template grouping rows)
  // carry no values and are skipped.
  const totals = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of rows) {
      if (r.isSubHeading) continue;
      const cd = workspace.class_data?.[r.classCode];
      if (!cd) continue;
      if (cd.hide_in_print) continue;
      const cv = cd.column_values;
      if (!cv) continue;
      for (const col of visibleColumns) {
        if (col.data_type !== 'amount') continue;
        const a = cv[col.code];
        const r2 = cv[col.code + ROUNDED_SUFFIX];
        if (typeof a === 'number') out[col.code] = (out[col.code] ?? 0) + a;
        if (typeof r2 === 'number') {
          const k = col.code + ROUNDED_SUFFIX;
          out[k] = (out[k] ?? 0) + r2;
        }
      }
    }
    return out;
  }, [rows, workspace.class_data, visibleColumns]);

  // Persist the live UI Total into workspace.column_totals so the PDF
  // renderer reads it as-is (no server-side sum). The setter at the
  // FsaNoteModal level shallow-compares and skips no-op writes to
  // avoid dirtying the workspace pointlessly on identity-only renders.
  useEffect(() => {
    // Band instances render a single class; the note-wide column_totals is
    // the Σ across ALL classes, computed once by MainNotePane. If each band
    // wrote here they'd overwrite each other (last writer wins).
    if (bandClassCode) return;
    setColumnTotals(totals);
  }, [totals, setColumnTotals, bandClassCode]);

  return (
    <div style={wrapperStyle}>
      <div style={tableScrollStyle}>
        <table ref={tableRef} style={tableStyle}>
          <colgroup>
            <col style={{ width: COL_LABEL_WIDTH }} />
            {visibleColumns.map(c => <col key={c.code} style={{ width: COL_AMOUNT_WIDTH }} />)}
            <col style={{ width: COL_ACTIONS_WIDTH }} />
          </colgroup>
          <thead style={theadStyle}>
            {hasGroupedHeader ? (
              <>
                {/* Top row — group banners. "Particulars" + actions
                    rowSpan=2 so they stay anchored across both header
                    rows. Each contiguous run of columns sharing the
                    same `group` value collapses into a single colSpan
                    cell. Ungrouped columns get an individual rowSpan=2
                    header so they still occupy both rows. */}
                <tr>
                  <th rowSpan={2} style={{ ...thLabelStyle, ...stickyLabelStyle }}>Particulars</th>
                  {groupHeaderCells.map((g, i) => {
                    if (g.kind === 'span') {
                      // Single ungrouped column — span both rows here.
                      const c = g.col;
                      const baseBg = 'var(--grid-header-bg, #dbeafe)';
                      const sticky = getStickyRightStyle(stickyRightMap.get(c.code), baseBg, 4);
                      return (
                        <th
                          key={`g-${i}-${c.code}`}
                          rowSpan={2}
                          style={{
                            ...thAmountStyle,
                            ...(c.is_total ? thTotalStyle : null),
                            ...(c.is_prior_year ? thPriorStyle : null),
                            ...(sticky ?? null),
                          }}
                          title={c.is_total ? 'Rolls up to the Note closing total — referenced by the BS line.'
                               : c.is_prior_year ? 'Prior-year comparative.'
                               : c.is_computed ? `Computed: ${c.formula ?? ''}` : undefined}
                        >
                          {c.label}
                          {c.is_computed && !c.is_total && <span style={computedTagStyle}>ƒ</span>}
                        </th>
                      );
                    }
                    // Group banner — colSpan across its member columns.
                    return (
                      <th
                        key={`g-${i}-${g.label}`}
                        colSpan={g.colCount}
                        style={{
                          ...thAmountStyle,
                          ...thGroupBannerStyle,
                        }}
                      >
                        {g.label}
                      </th>
                    );
                  })}
                  <th rowSpan={2} style={{
                    ...thActionStyle,
                    ...getStickyRightStyle(ACTIONS_STICKY_RIGHT_INFO, 'var(--grid-header-bg, #dbeafe)', 4),
                  }} />
                </tr>
                {/* Bottom row — only the sub-labels for columns that
                    sit under a group banner. Ungrouped columns were
                    already emitted via rowSpan=2 above and are skipped
                    here so QuestPDF's row-major cell flow stays aligned. */}
                <tr>
                  {visibleColumns.map(c => {
                    if (!c.group) return null;
                    const baseBg = 'var(--grid-header-bg, #dbeafe)';
                    const sticky = getStickyRightStyle(stickyRightMap.get(c.code), baseBg, 4);
                    return (
                      <th
                        key={c.code}
                        style={{
                          ...thAmountStyle,
                          ...thSubLabelStyle,
                          ...(c.is_total ? thTotalStyle : null),
                          ...(c.is_prior_year ? thPriorStyle : null),
                          ...(sticky ?? null),
                        }}
                        title={c.is_total ? 'Rolls up to the Note closing total — referenced by the BS line.'
                             : c.is_prior_year ? 'Prior-year comparative.'
                             : c.is_computed ? `Computed: ${c.formula ?? ''}` : undefined}
                      >
                        {c.label}
                        {c.is_computed && <span style={computedTagStyle}>ƒ</span>}
                      </th>
                    );
                  })}
                </tr>
              </>
            ) : (
            <tr>
              <th style={{ ...thLabelStyle, ...stickyLabelStyle }}>Particulars</th>
              {visibleColumns.map(c => {
                const baseBg = 'var(--grid-header-bg, #dbeafe)';
                const sticky = getStickyRightStyle(stickyRightMap.get(c.code), baseBg, 4);
                return (
                  <th
                    key={c.code}
                    style={{
                      ...thAmountStyle,
                      ...(c.is_total ? thTotalStyle : null),
                      ...(c.is_prior_year ? thPriorStyle : null),
                      ...(sticky ?? null),
                    }}
                    title={c.is_total ? 'Rolls up to the Note closing total — referenced by the BS line.'
                         : c.is_prior_year ? 'Prior-year comparative.'
                         : c.is_computed ? `Computed: ${c.formula ?? ''}` : undefined}
                  >
                    {c.label}
                    {c.is_computed && !c.is_total && <span style={computedTagStyle}>ƒ</span>}
                  </th>
                );
              })}
              <th style={{
                ...thActionStyle,
                ...getStickyRightStyle(ACTIONS_STICKY_RIGHT_INFO, 'var(--grid-header-bg, #dbeafe)', 4),
              }} />
            </tr>
            )}
          </thead>
          <tbody>
            {rows.map(r => {
              const classLedgers = classes.find(c => c.classCode === r.classCode)?.ledgers ?? [];
              // Pre-compute reconciliation target for this row, if any.
              // Sign convention: matrix amounts are auditor-typed positive
              // values. Stored class_data.amount_actual on credit-balance
              // schedules is signed negative. Apply reconcileSign so the
              // chip's drift = matrix_total − sign_flipped_target
              // (and a clean match doesn't surface as 2× the value).
              let rTargetAmount: number | null = null;
              let rTargetLabel: string | undefined;
              if (r.reconcileWithClass) {
                const t = workspace.class_data?.[r.reconcileWithClass]?.amount_actual;
                rTargetAmount = (typeof t === 'number' ? t : 0) * reconcileSign;
                const targetCls = classes.find(c => c.classCode === r.reconcileWithClass);
                rTargetLabel = targetCls?.className ?? r.reconcileWithClass;
              } else if (r.reconcileWithTotal) {
                rTargetAmount = noteClassifiedTotal * reconcileSign;
                rTargetLabel = 'Note total';
              }
              return (
                <ClassSection
                  key={r.classCode}
                  row={r}
                  visibleColumns={visibleColumns}
                  workspace={effectiveWorkspace}
                  displayMode={displayMode}
                  roundingFactorKey={roundingFactorKey}
                  live={liveByClassCode.get(r.classCode) ?? null}
                  liveLedgers={classLedgers}
                  isCustom={customClassCodes.has(r.classCode)}
                  remarksOpen={remarksOpen.has(r.classCode)}
                  groupingDimension={groupingDimension ?? null}
                  editingHere={editing && editing.classCode === r.classCode ? editing : null}
                  stickyRightMap={stickyRightMap}
                  onCellChange={handleCellChange}
                  applyLedgerSnapshot={applyLedgerSnapshot}
                  setClassHideInPrint={setClassHideInPrint}
                  onStartRenameTemplate={onStartRenameTemplate}
                  onStartRenameCustom={onStartRenameCustom}
                  deleteCustomClass={deleteCustomClass}
                  setClassField={setClassField}
                  setClassDimensionValue={setClassDimensionValue}
                  toggleRemarks={toggleRemarks}
                  onStartAddLineItem={handleStartAddLineItem}
                  onStartEditLineItem={handleStartEditLineItem}
                  onLineItemCellChange={handleLineItemCellChange}
                  onToggleLineItemRemarks={toggleLineItemRemarks}
                  onSetLineItemRemarks={setLineItemRemarks}
                  lineItemRemarksOpen={lineItemRemarksOpen}
                  onEditorSave={handleEditorSave}
                  onEditorCancel={handleEditorCancel}
                  deleteLineItem={handleDeleteLineItem}
                  totalColumns={totalColumns}
                  readOnly={readOnly}
                  reconcileTargetAmount={rTargetAmount}
                  reconcileTargetLabel={rTargetLabel}
                  reconcileTolerance={reconcileTolerance}
                  sectionLayout={sectionLayout}
                  pyLayout={pyLayout}
                  onClassPyCellChange={handleClassPyCellChange}
                  isPyComputedColumn={isPyComputedColumn}
                  hideBanner={!!bandClassCode}
                  allowPriorYearInput={allowPriorYearInput}
                />
              );
            })}
          </tbody>
          <tfoot>
            {showTotalRow && !sectionLayout && (
            <tr>
              <td style={{ ...footerLabelStyle, ...stickyFooterLabelStyle }}>Total</td>
              {visibleColumns.map(c => {
                const key = isRounded ? c.code + ROUNDED_SUFFIX : c.code;
                const v = c.data_type === 'amount' ? totals[key] ?? 0 : null;
                const baseBg = 'var(--bg-secondary, #f8fafc)';
                const sticky = getStickyRightStyle(stickyRightMap.get(c.code), baseBg, 3);
                // Drift chip on the Total row is intentionally OMITTED.
                // If individual class / line item rows have drift, the
                // auditor already sees per-row chips on those — a second
                // chip on the column total would only echo the same gap.
                // Keeping the Total row clean preserves it as a pure
                // arithmetic summary (Σ class column_values).
                return (
                  <td
                    key={c.code}
                    style={{
                      ...footerAmountStyle,
                      ...(sticky ?? null),
                    }}
                  >
                    {v == null ? '' : fmtAmt(v)}
                  </td>
                );
              })}
              <td style={{
                ...footerAmountStyle,
                ...getStickyRightStyle(ACTIONS_STICKY_RIGHT_INFO, 'var(--bg-secondary, #f8fafc)', 3),
              }} />
            </tr>
            )}
            {/*
             * Previous Year Total — auditor-entered, manually transcribed
             * from the prior year's published statements. Independent of
             * the current-year class rollup. Same display-mode semantics
             * as the rest of the table: actual mode reads/writes
             * prior_year_totals[code]; rounded mode reads/writes the
             * <code>__rounded</code> companion. The Net Block PY column
             * (already a prior-year column) reuses the same row — no
             * special casing.
             *
             * Hidden when py_layout === "separate_grid" — ageing matrices
             * render PY as a full second grid below the CY block instead
             * (planned v2 work).
             */}
            {pyLayout === 'inline_columns' && !sectionLayout && (
            <tr>
              <td style={{
                ...footerLabelStyle,
                ...stickyFooterLabelStyle,
                ...priorYearLabelStyle,
              }}>
                Previous Year Total
              </td>
              {visibleColumns.map(c => {
                const isAmount = c.data_type === 'amount';
                const baseBg = 'var(--bg-secondary, #f8fafc)';
                const sticky = getStickyRightStyle(stickyRightMap.get(c.code), baseBg, 3);
                const tdStyle = {
                  ...footerAmountStyle,
                  ...priorYearAmountStyle,
                  ...(sticky ?? null),
                };
                if (!isAmount) {
                  return <td key={c.code} style={tdStyle} />;
                }
                const actualKey = c.code;
                const roundedKey = c.code + ROUNDED_SUFFIX;
                const pyt = workspace.prior_year_totals ?? null;
                const actualVal = pyt?.[actualKey] ?? 0;
                const roundedVal = pyt?.[roundedKey];
                const displayValue = isRounded
                  ? (roundedVal != null ? roundedVal : autoRound(actualVal, roundingFactorKey))
                  : actualVal;
                const editTargetCode = isRounded ? roundedKey : actualKey;
                // Computed columns (is_computed+formula OR drift_formula)
                // are derived by the PY recompute pass — render as read-only
                // text in BOTH modes. Auditor input columns remain editable.
                const isComputed = isPyComputedColumn(c);
                const cellIsReadOnly = readOnly || isComputed;
                return (
                  <td key={c.code} style={tdStyle}>
                    {cellIsReadOnly ? (
                      <span style={amountReadOnlyStyle}>{fmtAmt(displayValue)}</span>
                    ) : (
                      <NumericInput
                        className="form-input"
                        value={displayValue}
                        onChange={v => handlePyCellChange(editTargetCode, v)}
                        style={amountInputStyle}
                      />
                    )}
                  </td>
                );
              })}
              <td style={{
                ...footerAmountStyle,
                ...priorYearAmountStyle,
                ...getStickyRightStyle(ACTIONS_STICKY_RIGHT_INFO, 'var(--bg-secondary, #f8fafc)', 3),
              }} />
            </tr>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// Row component — kept separate so memoisation can kick in per class
// when only one row's values change.
// ============================================================================

// ============================================================================
// ClassSection — emits the class row + (optional) remarks row + line item
// rows + delete affordances. Returns a React Fragment so multiple <tr>
// children flow into the parent <tbody> seamlessly.
// ============================================================================

interface EditingState {
  classCode: string;
  localId: string | null;
  draft: LineItemDraft;
}

const ClassSection: React.FC<{
  row: RenderRow;
  visibleColumns: FormatColumnSpec[];
  workspace: NoteWorkspaceData;
  displayMode: 'actual' | 'rounded';
  roundingFactorKey: string;
  live: { cy: number; py: number } | null;
  /** Live TB ledgers classified to this class — drives the shared LineItemEditor's ledger picker. */
  liveLedgers: NoteLedger[];
  isCustom: boolean;
  remarksOpen: boolean;
  groupingDimension: NoteGroupingDimension | null;
  /** Set when this class has the inline shared editor open (add or edit). */
  editingHere: EditingState | null;
  /** Sticky-right offset map for Net Block CY / PY columns. Action column handled separately. */
  stickyRightMap: Map<string, StickyRightInfo>;
  onCellChange: (classCode: string, colCode: string, value: number) => void;
  applyLedgerSnapshot: (classCode: string) => void;
  setClassHideInPrint: (classCode: string, hide: boolean) => void;
  onStartRenameTemplate: (classCode: string, currentLabel: string) => void;
  onStartRenameCustom: (localId: string, currentName: string) => void;
  deleteCustomClass: (localId: string) => void;
  setClassField: <K extends keyof NoteClassData>(classCode: string, field: K, value: NoteClassData[K]) => void;
  setClassDimensionValue: (classCode: string, dimCode: string, value: string | null) => void;
  toggleRemarks: (classCode: string) => void;
  onStartAddLineItem: (classCode: string) => void;
  onStartEditLineItem: (classCode: string, li: NoteLineItem) => void;
  /** Inline cell edit on a line item — applies to movement / computed columns and (in rounded mode) Net Block. */
  onLineItemCellChange: (classCode: string, localId: string, colCode: string, value: number) => void;
  /** Toggles per-line-item remarks editor row. */
  onToggleLineItemRemarks: (classCode: string, localId: string) => void;
  /** Persists per-line-item remarks (writes li.remarks via setLineItems). */
  onSetLineItemRemarks: (classCode: string, localId: string, text: string) => void;
  /** Set of `${classCode}::${localId}` keys currently showing the remarks editor. */
  lineItemRemarksOpen: Set<string>;
  onEditorSave: (draft: LineItemDraft) => void;
  onEditorCancel: () => void;
  deleteLineItem: (classCode: string, localId: string) => void;
  totalColumns: number;
  readOnly: boolean;
  /** Pre-computed reconciliation target for an ageing-matrix subtotal row. */
  reconcileTargetAmount?: number | null;
  reconcileTargetLabel?: string;
  reconcileTolerance?: number;
  /** Section-layout mode — render this class as its own section (header +
   *  line items + per-section Total + per-section Previous Year Total). */
  sectionLayout?: boolean;
  pyLayout?: 'inline_columns' | 'separate_grid';
  /** Per-section Previous Year Total edit (section_layout only). */
  onClassPyCellChange?: (classCode: string, colCode: string, value: number) => void;
  isPyComputedColumn?: (c: FormatColumnSpec) => boolean;
  /** Suppress the in-table section banner — the parent AgeingSectionBand
   *  already names this section (band mode). */
  hideBanner?: boolean;
  /** TRUE → a plain is_prior_year column (Opening) accepts manual input
   *  (no PY-linked engagement to source it from). */
  allowPriorYearInput?: boolean;
}> = ({
  row, visibleColumns, workspace, displayMode, roundingFactorKey, live, liveLedgers,
  isCustom, remarksOpen, groupingDimension, editingHere, stickyRightMap,
  onCellChange, applyLedgerSnapshot, setClassHideInPrint,
  onStartRenameTemplate, onStartRenameCustom, deleteCustomClass,
  setClassField, setClassDimensionValue, toggleRemarks,
  onStartAddLineItem, onStartEditLineItem, onLineItemCellChange,
  onToggleLineItemRemarks, onSetLineItemRemarks, lineItemRemarksOpen,
  onEditorSave, onEditorCancel,
  deleteLineItem,
  totalColumns, readOnly,
  reconcileTargetAmount, reconcileTargetLabel, reconcileTolerance,
  sectionLayout, pyLayout, onClassPyCellChange, isPyComputedColumn, hideBanner,
  allowPriorYearInput,
}) => {
  const cd = workspace.class_data?.[row.classCode];
  const isRounded = displayMode === 'rounded';
  const indent = Math.min(row.depth, 4) * 14;
  const hidden = cd?.hide_in_print === true;
  const lineItems = cd?.line_items ?? [];
  const hasLineItems = lineItems.length > 0;
  const remarksText = cd?.remarks ?? '';
  const showRemarksRow = remarksOpen || !!remarksText.trim();

  // Action menu — parity with the standard 2-col pane's per-class menu.
  // Items are added conditionally so custom-only / dimension-only ones
  // don't appear on rows that can't use them.
  const actions: ActionMenuItem[] = [];
  actions.push({
    id: 'refresh-ledger',
    label: 'Update from Ledger',
    icon: <IconSystem.refresh size={14} />,
    onClick: () => applyLedgerSnapshot(row.classCode),
    disabled: readOnly || hasLineItems,
  });
  actions.push({
    id: 'add-line-item',
    label: 'Add Line Item',
    icon: <IconSystem.add size={14} />,
    onClick: () => onStartAddLineItem(row.classCode),
    disabled: readOnly,
  });
  actions.push({
    id: 'toggle-remarks',
    label: showRemarksRow && remarksText ? 'Edit Note Text' : 'Add Note Text',
    icon: <IconSystem.file size={14} />,
    onClick: () => toggleRemarks(row.classCode),
    disabled: readOnly,
  });
  actions.push({
    id: 'toggle-hide',
    label: hidden ? 'Show in PDF' : 'Hide in PDF',
    icon: <IconSystem.eye size={14} />,
    onClick: () => setClassHideInPrint(row.classCode, !hidden),
    disabled: readOnly,
  });
  actions.push({
    id: 'rename',
    label: isCustom ? 'Rename Custom Class…' : 'Edit class label…',
    icon: <IconSystem.edit size={14} />,
    onClick: () => isCustom
      ? onStartRenameCustom(row.classCode, row.label)
      : onStartRenameTemplate(row.classCode, row.label),
    disabled: readOnly,
  });
  // Dimension setter — only when the schedule declares a grouping
  // dimension (Trade Receivables / Payables Disputed-Undisputed etc.).
  // PPE doesn't have one, so the item just doesn't render.
  if (groupingDimension && !readOnly) {
    const current = cd?.dimension_values?.[groupingDimension.code];
    for (const opt of groupingDimension.options) {
      const isActive = current === opt.code;
      actions.push({
        id: `dim-${opt.code}`,
        label: `${groupingDimension.label}: ${opt.label}${isActive ? '  ✓' : ''}`,
        icon: <IconSystem.tag size={14} />,
        onClick: () => setClassDimensionValue(
          row.classCode,
          groupingDimension.code,
          isActive ? null : opt.code,
        ),
      });
    }
  }
  if (isCustom) {
    actions.push({
      id: 'delete-custom',
      label: 'Delete Custom Class',
      icon: <IconSystem.trash size={14} />,
      onClick: () => deleteCustomClass(row.classCode),
      variant: 'danger',
      disabled: readOnly,
    });
  }

  if (row.isSubHeading) {
    return (
      <tr>
        <td colSpan={totalColumns} style={{ ...subHeadingStyle, paddingLeft: 12 + indent }}>
          {row.label}
        </td>
      </tr>
    );
  }

  return (
    <>
      {sectionLayout ? (
        hideBanner ? (
          // Band mode: the parent AgeingSectionBand header already names the
          // section. Render just a slim row carrying the per-class action
          // menu (Add Line Item, etc.) so the affordance stays reachable.
          !readOnly ? (
            <tr>
              <td colSpan={Math.max(1, totalColumns - 1)} style={{ padding: '2px 8px' }} />
              <td style={{ padding: '2px 8px', textAlign: 'right' }}>
                <ActionMenu items={actions} position="bottom-right" />
              </td>
            </tr>
          ) : null
        ) : (
        // Section-layout header: the class is a SECTION banner (label + its
        // action menu), NOT a directly-editable data row. Amounts come from
        // the line items (partners) below and are summed in the per-section
        // Total row that this section renders at its foot.
        <tr>
          <td colSpan={Math.max(1, totalColumns - 1)} style={{ ...subHeadingStyle, paddingLeft: 12 + indent }}>
            {row.label}
          </td>
          <td style={{ ...subHeadingStyle, textAlign: 'right', paddingRight: 8 }}>
            {!readOnly && <ActionMenu items={actions} position="bottom-right" />}
          </td>
        </tr>
        )
      ) : (
        <ClassDataRow
          row={row}
          visibleColumns={visibleColumns}
          cd={cd}
          displayMode={displayMode}
          roundingFactorKey={roundingFactorKey}
          live={live}
          hidden={hidden}
          hasLineItems={hasLineItems}
          isCustom={isCustom}
          actions={actions}
          stickyRightMap={stickyRightMap}
          onCellChange={onCellChange}
          readOnly={readOnly}
          reconcileTargetAmount={reconcileTargetAmount}
          reconcileTargetLabel={reconcileTargetLabel}
          reconcileTolerance={reconcileTolerance}
          allowPriorYearInput={allowPriorYearInput}
        />
      )}
      {showRemarksRow && (
        <tr>
          <td colSpan={totalColumns} style={remarksRowStyle}>
            <textarea
              className="form-input"
              value={remarksText}
              onChange={e => setClassField(row.classCode, 'remarks', e.target.value)}
              placeholder={`Note text for "${row.label}" — prints italic under the row in the PDF.`}
              disabled={readOnly}
              autoFocus={remarksOpen && !remarksText}
              style={remarksTextareaStyle}
            />
          </td>
        </tr>
      )}
      {lineItems.map(li => (
        <LineItemRow
          key={li.local_id}
          li={li}
          classCode={row.classCode}
          visibleColumns={visibleColumns}
          displayMode={displayMode}
          roundingFactorKey={roundingFactorKey}
          indent={indent}
          totalColumns={totalColumns}
          remarksOpen={lineItemRemarksOpen.has(`${row.classCode}::${li.local_id}`)}
          stickyRightMap={stickyRightMap}
          onStartEdit={onStartEditLineItem}
          onCellChange={onLineItemCellChange}
          onToggleRemarks={onToggleLineItemRemarks}
          onSetRemarks={onSetLineItemRemarks}
          onDelete={deleteLineItem}
          readOnly={readOnly}
          allowPriorYearInput={allowPriorYearInput}
        />
      ))}
      {/* Shared LineItemEditor — opens below the class's line items on
          Add or Edit. Identical to what the standard 2-col pane uses;
          owns name + source + Net Block CY/PY. Movement detail is
          entered later as inline cell edits on the row. */}
      {editingHere && (
        <tr>
          <td colSpan={totalColumns} style={editorHostStyle}>
            <SharedLineItemEditor
              className={row.label}
              ledgers={liveLedgers as unknown as Parameters<typeof SharedLineItemEditor>[0]['ledgers']}
              referencedLedgerIds={new Set(
                lineItems
                  .filter(li => li.local_id !== editingHere.localId)
                  .flatMap(li => (li.ledgers ?? []).map(l => String(l.ledger_id))),
              )}
              editingLocalId={editingHere.localId}
              draft={editingHere.draft}
              onCancel={onEditorCancel}
              onSave={onEditorSave}
            />
          </td>
        </tr>
      )}
      {sectionLayout && (
        <>
          {/* Per-section Total — Σ of this section's line items (the class
              column_values rollup). Read-only; mirrors the note-wide footer
              Total but scoped to this one section. */}
          <tr>
            <td style={{ ...footerLabelStyle, paddingLeft: 12 + indent }}>Total</td>
            {visibleColumns.map(col => {
              const key = isRounded ? col.code + ROUNDED_SUFFIX : col.code;
              const v = col.data_type === 'amount' ? (cd?.column_values?.[key] ?? 0) : null;
              return (
                <td key={col.code} style={footerAmountStyle}>
                  {v == null ? '' : fmtAmt(v)}
                </td>
              );
            })}
            <td style={footerAmountStyle} />
          </tr>
          {/* Per-section Previous Year Total — auditor-entered comparative,
              stored per class in class_data[code].prior_year_totals. */}
          {pyLayout !== 'separate_grid' && (
            <tr>
              <td style={{ ...footerLabelStyle, ...priorYearLabelStyle, paddingLeft: 12 + indent }}>
                Previous Year Total
              </td>
              {visibleColumns.map(col => {
                const tdStyle = { ...footerAmountStyle, ...priorYearAmountStyle };
                if (col.data_type !== 'amount') return <td key={col.code} style={tdStyle} />;
                const actualKey = col.code;
                const roundedKey = col.code + ROUNDED_SUFFIX;
                const pyt = cd?.prior_year_totals ?? null;
                const actualVal = pyt?.[actualKey] ?? 0;
                const roundedVal = pyt?.[roundedKey];
                const displayValue = isRounded
                  ? (roundedVal != null ? roundedVal : autoRound(actualVal, roundingFactorKey))
                  : actualVal;
                const editTargetCode = isRounded ? roundedKey : actualKey;
                const cellIsReadOnly = readOnly || (isPyComputedColumn ? isPyComputedColumn(col) : false);
                return (
                  <td key={col.code} style={tdStyle}>
                    {cellIsReadOnly ? (
                      <span style={amountReadOnlyStyle}>{fmtAmt(displayValue)}</span>
                    ) : (
                      <NumericInput
                        className="form-input"
                        value={displayValue}
                        onChange={val => onClassPyCellChange?.(row.classCode, editTargetCode, val)}
                        style={amountInputStyle}
                      />
                    )}
                  </td>
                );
              })}
              <td style={{ ...footerAmountStyle, ...priorYearAmountStyle }} />
            </tr>
          )}
        </>
      )}
    </>
  );
};

// ----------------------------------------------------------------------
// Class row — single <tr>. Inputs go read-only when line items exist
// (the class total is Σ line items via Option A).
// ----------------------------------------------------------------------

const ClassDataRow: React.FC<{
  row: RenderRow;
  visibleColumns: FormatColumnSpec[];
  cd: NoteClassData | undefined;
  displayMode: 'actual' | 'rounded';
  roundingFactorKey: string;
  live: { cy: number; py: number } | null;
  hidden: boolean;
  hasLineItems: boolean;
  isCustom: boolean;
  actions: ActionMenuItem[];
  stickyRightMap: Map<string, StickyRightInfo>;
  onCellChange: (classCode: string, colCode: string, value: number) => void;
  readOnly: boolean;
  /**
   * Ageing-matrix reconciliation target. When set, this row's Total-column
   * value is compared against it; a drift chip surfaces in the label cell
   * when the difference exceeds `reconcileTolerance`. The parent computes
   * the right target per row (TB-classified leaf amount for
   * reconcile_with_class; Σ classified leaves for reconcile_with_total).
   */
  reconcileTargetAmount?: number | null;
  reconcileTargetLabel?: string;
  reconcileTolerance?: number;
  /** TRUE → plain is_prior_year column (Opening) editable in actuals (no PY-linked engagement). */
  allowPriorYearInput?: boolean;
}> = ({
  row, visibleColumns, cd, displayMode, roundingFactorKey, live,
  hidden, hasLineItems, isCustom, actions, stickyRightMap, onCellChange, readOnly,
  reconcileTargetAmount, reconcileTargetLabel, reconcileTolerance,
  allowPriorYearInput,
}) => {
  const cv = cd?.column_values ?? null;
  const isRounded = displayMode === 'rounded';
  const indent = Math.min(row.depth, 4) * 14;

  // Row background colour — used by sticky-right cells to paint
  // opaquely over scrolling content beneath.
  const rowBg = hidden
    ? 'var(--bg-primary, #ffffff)' // hidden rows use bg-primary with opacity 0.55 on tr; cells stay opaque
    : 'var(--bg-primary, #ffffff)';

  // Per-row drift chip — one per drift_formula column. With Net Block as a
  // strict-formula cell (`drift_formula` now drives the displayed value),
  // the chip's role flips: it compares the formula result against the live
  // TB ledger sum classified under this class. Drift > tolerance means the
  // auditor's typed movements don't explain the full TB classification —
  // either more movements are missing, or some TB ledgers should be
  // reclassified.
  //
  // The chip suppresses itself when |drift| ≤ engagement rounding tolerance
  // (paisa-precision compare here; the ₹1.00 engagement tolerance covers
  // the rest at save time).
  const driftChips = useMemo(() => {
    const out: Array<{ key: string; amount: number; label: string; tooltip: string }> = [];
    const cvForFormula = cv ?? {};
    for (const col of visibleColumns) {
      if (!(col.is_total || col.is_prior_year)) continue;
      if (!col.drift_formula) continue;
      const computed = evaluateFormula(col.drift_formula, cvForFormula);
      const source = live != null ? (col.is_total ? live.cy : live.py) : null;
      if (source == null) continue;
      const diff = source - computed;
      // Paisa-precision compare (suppresses sub-paisa FP residue).
      if (Math.round(diff * 100) === 0) continue;
      out.push({
        key: col.code,
        amount: diff,
        label: col.is_prior_year ? 'PY drift' : 'drift',
        tooltip: `${col.label}: movements compute to ${fmtAmt(computed)} but TB ledgers classify ${fmtAmt(source)} under this class. Drift = ${fmtAmt(diff)}.`,
      });
    }
    return out;
  }, [cv, visibleColumns, live]);

  // Reconciliation drift chip — surfaces when an ageing-matrix subtotal
  // row's Total column drifts from its declared target (a TB-classified
  // leaf's amount_actual for reconcile_with_class; the Note total for
  // reconcile_with_total). Pure display; no auto-correct.
  const reconcileChip = useMemo(() => {
    if (reconcileTargetAmount == null) return null;
    // Find the row's Total — first column with is_total true, or the
    // last computed amount column, or a column named 'total'.
    const totalCol = visibleColumns.find(c => c.is_total === true)
      ?? visibleColumns.find(c => c.code === 'total')
      ?? visibleColumns.filter(c => c.data_type === 'amount').slice(-1)[0];
    if (!totalCol) return null;
    const rowTotal = cv?.[totalCol.code];
    const rt = typeof rowTotal === 'number' ? rowTotal : 0;
    const diff = rt - reconcileTargetAmount;
    const tol = reconcileTolerance ?? 1.0;
    if (Math.abs(diff) <= tol) return null;
    return {
      amount: diff,
      label: 'recon drift',
      tooltip: `Row total ${fmtAmt(rt)} doesn't reconcile with ${reconcileTargetLabel ?? 'target'} ${fmtAmt(reconcileTargetAmount)}. Drift = ${fmtAmt(diff)}.`,
    };
  }, [reconcileTargetAmount, reconcileTargetLabel, reconcileTolerance, cv, visibleColumns]);

  return (
    <tr style={hidden ? hiddenRowStyle : rowStyle}>
      <td style={{
        ...labelTdStyle,
        ...stickyLabelTdStyle,
        paddingLeft: 12 + indent,
        ...(hidden ? hiddenLabelStyle : null),
      }}>
        <span style={labelTextStyle}>{row.label}</span>
        {isCustom && <CustomClassChip style={chipMarginStyle} />}
        {hidden && <HiddenInPrintChip style={chipMarginStyle} />}
        {hasLineItems && <RollupChip style={chipMarginStyle} />}
        {driftChips.map(d => (
          <DriftChip key={d.key} amount={d.amount} label={d.label} tooltip={d.tooltip} style={chipMarginStyle} />
        ))}
        {reconcileChip && (
          <DriftChip
            amount={reconcileChip.amount}
            label={reconcileChip.label}
            tooltip={reconcileChip.tooltip}
            style={chipMarginStyle}
          />
        )}
      </td>
      {visibleColumns.map(col => {
        const baseStyle = col.is_total ? amountTdTotalStyle
                        : col.is_prior_year ? amountTdPriorStyle
                        : amountTdStyle;
        // Sticky-right freeze — Net Block CY / PY stay visible while
        // scrolling through movement detail. Per-column lookup.
        // No special accent for is_total; uniform with other amount cells.
        const stickyStyle = getStickyRightStyle(stickyRightMap.get(col.code), rowBg, 2);
        const isAuthoritativeTotalCol = col.is_total || col.is_prior_year;

        // Display value resolution per (mode × column kind × row state):
        //
        //                 │ actuals mode                  │ rounded mode
        // ────────────────┼───────────────────────────────┼─────────────────────────────────
        //   is_total      │ rolled-up sum (line items) OR │ saved __rounded OR autoRound(actuals)
        //   /prior_year   │ live TB (no line items) OR    │
        //                 │ saved override                │
        //   computed col  │ formula eval (in actuals dict)│ saved __rounded OR autoRound(actuals)
        //   input col     │ saved actual                  │ saved __rounded OR autoRound(actuals)
        //
        // The rounded mode falls back to autoRound on the fly so
        // engagement-factor-aware rendering works even when the saved
        // __rounded companion is absent or stale.
        let displayValue: number = 0;
        if (col.data_type === 'amount') {
          const actualForCol = (() => {
            if (isAuthoritativeTotalCol) {
              // Strict formula: Net Block CY/PY (drift_formula columns)
              // are evaluated at render time from the row's movement
              // inputs in cv. Saved-as-primary holds via Phase B writing
              // the same expression's result into cv on every edit; this
              // render path stays correct even on existing engagements
              // where cv[col.code] is a stale sourced value.
              if (col.drift_formula) return evaluateFormula(col.drift_formula, cv ?? {});
              if (hasLineItems) return cv?.[col.code] ?? 0;
              // Ageing-matrix Total columns are formula-driven and have
              // no live TB amount on the matrix rows (those classes
              // don't host ledgers). Read from cv to pick up either the
              // auditor-typed total (input row) or the synth value
              // injected by effectiveWorkspace (computed subtotal row).
              if (col.is_computed === true) return cv?.[col.code] ?? 0;
              if (live != null) return col.is_total ? live.cy : live.py;
              return cv?.[col.code] ?? 0;
            }
            return cv?.[col.code] ?? 0;
          })();
          if (isRounded) {
            const savedRounded = cv?.[col.code + ROUNDED_SUFFIX];
            displayValue = savedRounded != null
              ? savedRounded
              : autoRound(actualForCol, roundingFactorKey);
          } else {
            displayValue = actualForCol;
          }
        } else {
          displayValue = cv?.[col.code] ?? 0;
        }

        // Editability rules:
        //   • global readOnly                       → never editable
        //   • class has line items                  → read-only on EVERY
        //     cell (the class row is a pure rollup
        //     of its line items; auditor edits the
        //     line items)
        //   • computed in actuals                   → read-only (formula
        //                                              auto-evaluates)
        //   • computed in rounded                   → EDITABLE (audit
        //                                              independence — tolerance
        //                                              applies at save)
        //   • is_total / is_prior_year in actuals   → read-only (TB-sourced
        //                                              on the class row when
        //                                              no line items)
        //   • is_total / is_prior_year in rounded   → EDITABLE (auditor
        //                                              override; tolerance)
        // Formula-driven columns lock in BOTH modes (per FSA "calculated
        // values stay calculated" rule). Sourced-only is_total/is_prior_year
        // columns (no formula expression at all — none today in PPE, kept
        // for future extensibility) keep the actuals-only lock.
        const isFormulaDriven = (col.is_computed && col.formula) || col.drift_formula;
        // Plain is_prior_year (Opening) editable in actuals when there's no
        // PY-linked engagement to source it from.
        const priorYearEditable = !!col.is_prior_year && !isFormulaDriven && !!allowPriorYearInput;
        const isReadOnly = readOnly
          || !!col.read_only  // derived column (e.g. remuneration ← Remuneration note)
          || hasLineItems
          || row.isComputedSubtotal === true  // ageing-matrix computed subtotal — value is derived
          || !!isFormulaDriven
          || (isAuthoritativeTotalCol && !isFormulaDriven && !isRounded && !priorYearEditable);

        // Edit target: rounded mode writes the __rounded companion key
        // so the auditor's actual stays as-is (TB / rolled-up / typed).
        // recomputeRow's "skip auto-rounded if user is editing __rounded"
        // guard preserves the override.
        const editTargetCode = isRounded ? col.code + ROUNDED_SUFFIX : col.code;

        if (col.data_type !== 'amount') {
          return (
            <td key={col.code} style={{ ...baseStyle, ...(stickyStyle ?? null) }}>
              <span style={amountReadOnlyStyle}>{String(displayValue ?? '')}</span>
            </td>
          );
        }
        return (
          <td key={col.code} style={{ ...baseStyle, ...(stickyStyle ?? null) }}>
            {isReadOnly ? (
              <span style={amountReadOnlyStyle}>{fmtAmt(displayValue)}</span>
            ) : (
              <NumericInput
                className="form-input"
                value={displayValue}
                onChange={v => onCellChange(row.classCode, editTargetCode, v)}
                style={amountInputStyle}
              />
            )}
          </td>
        );
      })}
      <td style={{
        ...actionTdStyle,
        ...getStickyRightStyle(ACTIONS_STICKY_RIGHT_INFO, rowBg, 2),
      }}>
        <ActionMenu items={actions} position="bottom-right" />
      </td>
    </tr>
  );
};

// ----------------------------------------------------------------------
// Line item row — same column shape as the class, indented one level,
// with an inline rename input + inline delete button in the actions cell.
// ----------------------------------------------------------------------

const LineItemRow: React.FC<{
  li: NoteLineItem;
  classCode: string;
  visibleColumns: FormatColumnSpec[];
  displayMode: 'actual' | 'rounded';
  /** Engagement rounding factor — used by the on-the-fly autoRound fallback when a line item's `__rounded` companion hasn't been populated yet, and to round line-item cell rounded edits at write time. */
  roundingFactorKey: string;
  indent: number;
  /** Used by the expanded remarks editor row to span all columns. */
  totalColumns: number;
  /** TRUE when this row's remarks editor is currently visible. */
  remarksOpen: boolean;
  /** Sticky-right offsets for Net Block CY / PY columns. */
  stickyRightMap: Map<string, StickyRightInfo>;
  onStartEdit: (classCode: string, li: NoteLineItem) => void;
  onCellChange: (classCode: string, localId: string, colCode: string, value: number) => void;
  onToggleRemarks: (classCode: string, localId: string) => void;
  onSetRemarks: (classCode: string, localId: string, text: string) => void;
  onDelete: (classCode: string, localId: string) => void;
  readOnly: boolean;
  /** TRUE → plain is_prior_year column (Opening) editable in actuals (no PY-linked engagement). */
  allowPriorYearInput?: boolean;
}> = ({
  li, classCode, visibleColumns, displayMode, roundingFactorKey, indent,
  totalColumns, remarksOpen, stickyRightMap,
  onStartEdit, onCellChange, onToggleRemarks, onSetRemarks, onDelete, readOnly,
  allowPriorYearInput,
}) => {
  const cv = li.column_values ?? null;
  const isRounded = displayMode === 'rounded';
  const lineIndent = indent + 18; // one level deeper than the class

  const isLedgersMode = li.allocation_mode === 'ledgers';
  // Net Block (is_total / is_prior_year) cells are read-only in actuals
  // mode (sourced from editor → Manual amount OR Σ Ledgers). In rounded
  // mode every cell becomes editable (audit-independence override;
  // tolerance enforced at save).
  const isNetBlockCol = (col: FormatColumnSpec) => col.is_total || col.is_prior_year;

  const remarksText = li.remarks ?? '';
  const showRemarksRow = remarksOpen || !!remarksText.trim();

  // Action menu — parity with the standard pane's per-line-item menu.
  const actions: ActionMenuItem[] = [
    {
      id: 'edit-line',
      label: 'Edit line',
      icon: <IconSystem.edit size={14} />,
      onClick: () => onStartEdit(classCode, li),
      disabled: readOnly,
    },
    {
      id: 'toggle-remarks',
      label: showRemarksRow && remarksText ? 'Edit Note Text' : 'Add Note Text',
      icon: <IconSystem.file size={14} />,
      onClick: () => onToggleRemarks(classCode, li.local_id),
      disabled: readOnly,
    },
    {
      id: 'delete-line',
      label: 'Delete line',
      icon: <IconSystem.trash size={14} />,
      onClick: () => onDelete(classCode, li.local_id),
      variant: 'danger',
      disabled: readOnly,
    },
  ];

  // Per-line-item drift chip — formula result vs allocation_mode source.
  // The displayed Net Block on a line item is now the formula value
  // (gross_closing − dep_closing); the chip warns when that doesn't match
  // the line's allocation source:
  //   • ledgers     → Σ live ledger closing / opening (live recompute on TB changes)
  //   • manual      → li.manual_amount_actual (CY only; manual lines don't preserve a separate PY source)
  //   • cross_note  → li.cross_note_amount / li.prior_cross_note_amount
  //   • pl_flow     → no chip (PL state machine owns the value)
  const lineItemDriftChips = useMemo(() => {
    const out: Array<{ key: string; amount: number; label: string; tooltip: string }> = [];
    const cvForFormula = cv ?? {};
    const getSource = (kind: 'closing' | 'opening'): number | null => {
      switch (li.allocation_mode) {
        case 'ledgers':
          return (li.ledgers ?? []).reduce(
            (s, l) => s + (kind === 'closing' ? l.amount_actual : l.prior_amount_actual),
            0,
          );
        case 'manual':
          if (kind === 'opening') return null; // no preserved manual PY source
          return li.manual_amount_actual ?? 0;
        case 'cross_note':
          return kind === 'closing'
            ? (li.cross_note_amount ?? 0)
            : (li.prior_cross_note_amount ?? 0);
        case 'pl_flow':
          return null;
        default:
          return null;
      }
    };
    for (const col of visibleColumns) {
      if (!(col.is_total || col.is_prior_year)) continue;
      if (!col.drift_formula) continue;
      const computed = evaluateFormula(col.drift_formula, cvForFormula);
      const source = getSource(col.is_total ? 'closing' : 'opening');
      if (source == null) continue;
      const diff = source - computed;
      if (Math.round(diff * 100) === 0) continue;
      out.push({
        key: col.code,
        amount: diff,
        label: col.is_prior_year ? 'PY drift' : 'drift',
        tooltip: `${col.label}: movements compute to ${fmtAmt(computed)} but source is ${fmtAmt(source)}. Drift = ${fmtAmt(diff)}.`,
      });
    }
    return out;
  }, [cv, visibleColumns, li]);

  const sourceChipLabel = isLedgersMode
    ? `${(li.ledgers ?? []).length} ledgers`
    : 'Manual';
  const sourceChipTooltip = isLedgersMode
    ? `${(li.ledgers ?? []).length} ledger(s) summed for Net Block`
    : 'Net Block entered manually in the editor';

  return (
    <>
    <tr style={lineItemRowStyle}>
      <td style={{
        ...labelTdStyle,
        ...stickyLabelTdStyle,
        paddingLeft: 12 + lineIndent,
        background: 'var(--bg-secondary, #f8fafc)',
      }}>
        <span style={lineItemNameDisplayStyle}>
          {li.name || <em style={lineItemNamePlaceholderStyle}>(untitled line item)</em>}
        </span>
        <SourceChip label={sourceChipLabel} tooltip={sourceChipTooltip} style={chipMarginStyle} />
        {lineItemDriftChips.map(d => (
          <DriftChip key={d.key} amount={d.amount} label={d.label} tooltip={d.tooltip} style={chipMarginStyle} />
        ))}
      </td>
      {visibleColumns.map(col => {
        const baseStyle = col.is_total ? amountTdTotalStyle
                        : col.is_prior_year ? amountTdPriorStyle
                        : amountTdStyle;
        // Sticky-right paint colour matches the line-item row band —
        // uniform across all amount columns.
        const stickyStyle = getStickyRightStyle(stickyRightMap.get(col.code), 'var(--bg-secondary, #f8fafc)', 2);

        // Display value — actuals or rounded with on-the-fly fallback.
        let displayValue: number = 0;
        if (col.data_type === 'amount') {
          // Strict formula on is_total/is_prior_year columns with drift_formula:
          // evaluate at render time so the line shows the movement-derived value
          // even when cv carries a stale source amount from an earlier save.
          const actualForCol = (col.drift_formula && isNetBlockCol(col))
            ? evaluateFormula(col.drift_formula, cv ?? {})
            : (cv?.[col.code] ?? 0);
          if (isRounded) {
            const savedRounded = cv?.[col.code + ROUNDED_SUFFIX];
            displayValue = savedRounded != null
              ? savedRounded
              : autoRound(actualForCol, roundingFactorKey);
          } else {
            displayValue = actualForCol;
          }
        } else {
          displayValue = cv?.[col.code] ?? 0;
        }

        // Inline editability rules:
        //  • global readOnly                                → read-only
        //  • formula-driven (is_computed+formula OR drift_formula) → read-only
        //    in BOTH modes (calculated stays calculated)
        //  • sourced-only is_total/is_prior_year in actuals  → read-only
        //    (kept for future sourced-only columns; PPE's Net Block no
        //    longer hits this branch since it has drift_formula)
        //  • input movement cell                             → editable
        const isFormulaDriven = (col.is_computed && col.formula) || col.drift_formula;
        // A plain is_prior_year column (Opening) becomes editable in actuals
        // when there's no PY-linked engagement to source it from.
        const priorYearEditable = !!col.is_prior_year && !isFormulaDriven && !!allowPriorYearInput;
        const isReadOnly = readOnly
          || !!col.read_only  // derived column (e.g. remuneration ← Remuneration note)
          || !!isFormulaDriven
          || (isNetBlockCol(col) && !isFormulaDriven && !isRounded && !priorYearEditable);

        // Edit target — rounded mode writes the __rounded companion.
        const editTargetCode = isRounded ? col.code + ROUNDED_SUFFIX : col.code;

        if (col.data_type !== 'amount') {
          return <td key={col.code} style={{ ...baseStyle, ...(stickyStyle ?? null) }}><span style={amountReadOnlyStyle}>{String(displayValue ?? '')}</span></td>;
        }
        return (
          <td key={col.code} style={{ ...baseStyle, ...(stickyStyle ?? null) }}>
            {isReadOnly ? (
              <span style={amountReadOnlyStyle}>{fmtAmt(displayValue)}</span>
            ) : (
              <NumericInput
                className="form-input"
                value={displayValue}
                onChange={v => onCellChange(classCode, li.local_id, editTargetCode, v)}
                style={amountInputStyle}
              />
            )}
          </td>
        );
      })}
      <td style={{
        ...actionTdStyle,
        ...getStickyRightStyle(ACTIONS_STICKY_RIGHT_INFO, 'var(--bg-secondary, #f8fafc)', 2),
      }}>
        <ActionMenu items={actions} position="bottom-right" />
      </td>
    </tr>
    {showRemarksRow && (
      <tr>
        <td colSpan={totalColumns} style={lineItemRemarksRowStyle}>
          <textarea
            className="form-input"
            value={remarksText}
            onChange={e => onSetRemarks(classCode, li.local_id, e.target.value)}
            placeholder={`Note text for "${li.name || 'this line item'}" — prints italic under the row in the PDF.`}
            disabled={readOnly}
            autoFocus={remarksOpen && !remarksText}
            style={lineItemRemarksTextareaStyle}
          />
        </td>
      </tr>
    )}
    </>
  );
};

// ============================================================================
// Helpers
// ============================================================================

interface RenderRow {
  classCode: string;
  label: string;
  depth: number;
  isSubHeading: boolean;
  /**
   * Computed-row metadata for ageing-matrix style schedules.
   * When `isComputedSubtotal` is true, the row's column values are
   * derived from the named operand classes (computation_rule type=SUM)
   * and the auditor doesn't edit cells directly.
   */
  isComputedSubtotal?: boolean;
  /** Operand class codes when isComputedSubtotal — sums per column. */
  computedOperands?: string[];
  /**
   * When set, this row reconciles its Total-column value against
   * `workspace.class_data[reconcileWithClass].amount_actual`. Drift chip
   * surfaces on the row label. Used by Trade Payables / Receivables
   * ageing matrix subtotal rows.
   */
  reconcileWithClass?: string;
  /**
   * When true, the row's total reconciles against the Note's overall
   * classified total (Σ amount_actual across classification leaves
   * that aren't `hide_in_main_note`). Used by the grand-total row.
   */
  reconcileWithTotal?: boolean;
}

const fmtAmt = (v: number) =>
  v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ----------------------------------------------------------------------
// Sticky-right freeze — Net Block CY / Net Block PY + actions column
// stay visible while the auditor scrolls horizontally through the
// movement detail. Column widths are FIXED in the table's colgroup so
// the right offsets line up cleanly.
// ----------------------------------------------------------------------

const COL_LABEL_WIDTH   = 260;
const COL_AMOUNT_WIDTH  = 140;
const COL_ACTIONS_WIDTH = 56;

interface StickyRightInfo {
  /** px from the right edge of the table. The actions column is offset 0. */
  offset: number;
  /** TRUE for the leftmost column in the sticky-right group — drives a subtle inner shadow on the left edge so the freeze boundary reads visually. */
  isLeftmost: boolean;
}

/**
 * Return position-sticky styling for a cell that should freeze to the
 * right edge of the table. Caller supplies the row's background colour
 * so the cell paints opaquely over scrolling content beneath. Adds a
 * clean `border-left` on the leftmost frozen column so the boundary is
 * clearly visible AND overlapping content from scrolling cells (which
 * have content-overflow due to long numbers + tabular-nums) doesn't
 * bleed across the freeze boundary.
 *
 * `backgroundClip: 'padding-box'` ensures the cell's solid background
 * extends to the cell's padding box (not just the content box), which
 * matters when long numbers in adjacent scrolling cells overflow to
 * the right and would otherwise show through.
 */
function getStickyRightStyle(
  info: StickyRightInfo | undefined,
  bg: string,
  zIndex = 2,
): React.CSSProperties | null {
  if (!info) return null;
  const base: React.CSSProperties = {
    position: 'sticky',
    right: info.offset,
    zIndex,
    backgroundColor: bg,
    backgroundClip: 'padding-box',
  };
  if (info.isLeftmost) {
    base.borderLeft = '1px solid var(--border-color, #cbd5e1)';
  }
  return base;
}

/** Sticky-right info for the actions column — always frozen at right: 0. */
const ACTIONS_STICKY_RIGHT_INFO: StickyRightInfo = { offset: 0, isLeftmost: false };

// Local id generator for new line items. Mirrors the standard pane's
// pattern — a short prefixed token is enough since these ids are only
// unique within their class's line_items array. Replace with crypto.randomUUID()
// when we tighten id semantics.
function makeLocalId(): string {
  return 'li_' + Math.random().toString(36).slice(2, 11) + Date.now().toString(36).slice(-4);
}

// ============================================================================
// Styles
// ============================================================================

const wrapperStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
  border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden',
  background: 'var(--bg-primary, #ffffff)',
};
const tableScrollStyle: React.CSSProperties = {
  overflow: 'auto', flex: 1, minHeight: 0,
};
const tableStyle: React.CSSProperties = {
  // table-layout: fixed → browser strictly respects the colgroup
  // widths instead of auto-sizing from cell content. Required so the
  // sticky-right offsets line up with the actual rendered column
  // widths; without it, large numbers in a movement cell could push
  // the column wider than the 140px hint and a gap opens up behind
  // Net Block PY / CY when scrolling.
  borderCollapse: 'separate', borderSpacing: 0,
  tableLayout: 'fixed',
  fontSize: '0.85rem',
  background: 'var(--bg-primary, #ffffff)',
};
const theadStyle: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 3,
};
const thLabelStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: 'var(--text-primary, #1e293b)',
  background: 'var(--grid-header-bg, #dbeafe)',
  borderBottom: '2px solid var(--border-color, #e2e8f0)',
  whiteSpace: 'nowrap',
};
const thAmountStyle: React.CSSProperties = {
  ...thLabelStyle,
  textAlign: 'right',
};
// is_total / is_prior_year sub-label cells render with no extra
// accent — Net Block CY / PY look identical to the other movement
// sub-labels. The "Net Block" group banner above already signals the
// section; per-cell highlights were felt to over-emphasise the column.
const thTotalStyle: React.CSSProperties = {};
const thPriorStyle: React.CSSProperties = {};
// Two-row header — group banner sits on top, with a slightly darker
// background than the regular header cells so the banner/sub-label
// hierarchy reads at a glance. Centered text — banners span multiple
// columns and look balanced when centered above their member cells.
const thGroupBannerStyle: React.CSSProperties = {
  background: '#bfdbfe',
  color: '#1e3a5f',
  textAlign: 'center',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
};
// Sub-label cells in the bottom row — slightly smaller font so the
// banner reads as the dominant level.
const thSubLabelStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 600,
};
const computedTagStyle: React.CSSProperties = {
  marginLeft: 4, fontSize: '0.65rem', fontWeight: 700,
  color: '#7c3aed', fontStyle: 'italic',
};
// Sticky-left positioning for the label column. zIndex above body
// cells but below the header (which is zIndex: 3).
const stickyLabelStyle: React.CSSProperties = {
  position: 'sticky', left: 0, zIndex: 4,
};
const stickyLabelTdStyle: React.CSSProperties = {
  position: 'sticky', left: 0, zIndex: 2,
  background: 'var(--bg-primary, #ffffff)',
};
const stickyFooterLabelStyle: React.CSSProperties = {
  position: 'sticky', left: 0, zIndex: 2,
  background: 'var(--bg-secondary, #f8fafc)',
};
const rowStyle: React.CSSProperties = {
  background: 'var(--bg-primary, #ffffff)',
};
const labelTdStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  color: 'var(--text-primary, #1e293b)',
  // whiteSpace: 'normal' so the chips can wrap to a second line when
  // a row carries several badges (drift + custom + hidden). The label
  // text itself stays a single visual element via labelTextStyle.
  whiteSpace: 'normal',
};
const labelTextStyle: React.CSSProperties = {
  // Keep the class name visually contiguous with the badges that
  // follow — no wrap inside the name itself, only between name and
  // chips. Mirrors the standard 2-col pane's label layout.
  whiteSpace: 'nowrap',
};
const chipMarginStyle: React.CSSProperties = {
  marginLeft: 6,
};
const amountTdStyle: React.CSSProperties = {
  padding: '0.4rem 0.75rem',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  // Clip overflow within each amount cell so a long value in a
  // scrolling column can't render past its column boundary and bleed
  // across the sticky-right freeze area. Drift indicators are no
  // longer rendered inside the amount cell (they live inline on the
  // label cell now, via the shared <DriftChip>), so vertical clip is
  // safe again.
  overflow: 'hidden',
};
// Net Block CY (is_total) and Net Block PY (is_prior_year) body cells
// render with no extra accent — same look as the other movement
// columns. The "Net Block" group banner in the header above is the
// only visual cue distinguishing this section.
const amountTdTotalStyle: React.CSSProperties = {
  ...amountTdStyle,
  fontWeight: 600,
};
const amountTdPriorStyle: React.CSSProperties = {
  ...amountTdStyle,
};
const amountInputStyle: React.CSSProperties = {
  width: '100%', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
  padding: '3px 6px', fontSize: '0.85rem',
};
const amountReadOnlyStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
// Live TB subline rendered under is_total / is_prior_year cells. Muted
// caption colour — auditor reads it as a reconciliation hint, not a
// primary number.
const liveSublineStyle: React.CSSProperties = {
  marginTop: 2,
  fontSize: '0.66rem',
  color: 'var(--text-secondary, #64748b)',
  fontVariantNumeric: 'tabular-nums',
  fontStyle: 'italic',
};
// Action column — sticky-right would conflict with native horizontal
// scroll. Inline trailing cell instead; auditors scroll to it like any
// other column.
const thActionStyle: React.CSSProperties = {
  ...thLabelStyle,
  width: 56,
  textAlign: 'center',
  padding: '0.6rem 0.4rem',
};
const actionTdStyle: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  textAlign: 'center',
  width: 56,
};
// Hidden-in-print rows render muted so the auditor sees the exclusion
// at a glance without losing the row entirely (consistent with the
// standard 2-col table's treatment).
const hiddenRowStyle: React.CSSProperties = {
  background: 'var(--bg-primary, #ffffff)',
  opacity: 0.55,
};
const hiddenLabelStyle: React.CSSProperties = {
  fontStyle: 'italic',
};
// hiddenChipStyle + rollupChipStyle removed — replaced by shared
// <HiddenInPrintChip> and <RollupChip> from ../../shared/RowBadges.
const remarksRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary, #f8fafc)',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  padding: '6px 12px',
};
const remarksTextareaStyle: React.CSSProperties = {
  width: '100%', minHeight: 56, fontSize: '0.8rem', padding: '6px 8px',
  resize: 'vertical',
};
const lineItemRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary, #f8fafc)',
};
const lineItemNameInputStyle: React.CSSProperties = {
  width: '100%', fontSize: '0.82rem', padding: '3px 6px',
};
const lineItemDeleteBtnStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: '#dc2626', padding: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const lineItemEditBtnStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'var(--text-secondary)', padding: 4,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const lineItemActionsStyle: React.CSSProperties = {
  display: 'inline-flex', gap: 4, alignItems: 'center', justifyContent: 'center',
};
const lineItemNameDisplayStyle: React.CSSProperties = {
  fontSize: '0.82rem',
  fontWeight: 500,
  color: 'var(--text-primary)',
};
const lineItemNamePlaceholderStyle: React.CSSProperties = {
  fontStyle: 'italic',
  color: 'var(--text-secondary)',
};
// lineItemSourceChipStyle removed — replaced by shared <SourceChip>
// from ../../shared/RowBadges.
// Per-line-item remarks editor row — spans the full table width so the
// textarea is comfortably wide even when 10 movement columns push the
// table into horizontal scroll.
const lineItemRemarksRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary, #f8fafc)',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  padding: '6px 12px',
};
const lineItemRemarksTextareaStyle: React.CSSProperties = {
  width: '100%', minHeight: 48, fontSize: '0.8rem', padding: '6px 8px',
  resize: 'vertical',
};
// Hosts the shared LineItemEditor as a full-width row beneath the
// class's existing line items. Padded so the editor's own framing
// breathes inside the table band.
const editorHostStyle: React.CSSProperties = {
  background: 'var(--bg-secondary, #f8fafc)',
  padding: '6px 12px 12px 30px',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
};
// Source pills (Manual / Ledgers) — small segmented control under the
// line-item name input. Matches the standard pane's source-selector
// shape so auditors recognise the pattern.
const sourcePillsRowStyle: React.CSSProperties = {
  display: 'flex', gap: 4, alignItems: 'center',
};
function sourcePillStyle(active: boolean): React.CSSProperties {
  return {
    padding: '2px 8px',
    fontSize: '0.66rem',
    fontWeight: 600,
    background: active ? '#2563eb' : 'transparent',
    color: active ? '#ffffff' : 'var(--text-secondary)',
    border: '1px solid ' + (active ? '#2563eb' : 'var(--border-color)'),
    borderRadius: 4,
    cursor: 'pointer',
    letterSpacing: 0.2,
  };
}
// Ledger picker — expanded row beneath a Ledgers-mode line item.
// Spans the full table width so the auditor reads ledgers L→R without
// horizontal scrolling.
const ledgerPickerRowStyle: React.CSSProperties = {
  background: '#eff6ff',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  padding: '8px 12px',
};
const ledgerPickerEmptyStyle: React.CSSProperties = {
  fontSize: '0.78rem', fontStyle: 'italic', color: 'var(--text-secondary)',
};
const ledgerPickerGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: 4,
};
function ledgerPickerItemStyle(active: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '4px 8px',
    fontSize: '0.78rem',
    background: active ? '#dbeafe' : '#ffffff',
    border: '1px solid ' + (active ? '#2563eb' : 'var(--border-color, #e2e8f0)'),
    borderRadius: 4,
    cursor: 'pointer',
  };
}
const ledgerPickerNameStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const ledgerPickerAmountStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
};
const subHeadingStyle: React.CSSProperties = {
  background: 'var(--bg-secondary, #f8fafc)',
  padding: '0.55rem 0.75rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  fontSize: '0.72rem',
  color: 'var(--text-secondary, #64748b)',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
};
const footerLabelStyle: React.CSSProperties = {
  padding: '0.55rem 0.75rem',
  background: 'var(--bg-secondary, #f8fafc)',
  fontWeight: 700,
  borderTop: '2px solid var(--border-color, #e2e8f0)',
};
const footerAmountStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  background: 'var(--bg-secondary, #f8fafc)',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  borderTop: '2px solid var(--border-color, #e2e8f0)',
  fontWeight: 600,
};
// footerTotalStyle removed — Net Block CY footer cell no longer carries
// a blue accent. The "Total" row label on the left is enough visual
// cue. Kept the constant name out of the codebase to avoid stale refs.

// Previous Year Total row — distinct visual band so it's clear this is a
// separate (manually-entered) row, not a continuation of the current-year
// Total. Single top border (no double rule) and a slightly different
// background tint.
const priorYearLabelStyle: React.CSSProperties = {
  borderTop: '1px solid var(--border-color, #e2e8f0)',
  color: 'var(--text-secondary, #64748b)',
  fontWeight: 600,
  background: 'var(--bg-primary, #ffffff)',
};
const priorYearAmountStyle: React.CSSProperties = {
  borderTop: '1px solid var(--border-color, #e2e8f0)',
  background: 'var(--bg-primary, #ffffff)',
  fontWeight: 500,
};

export default MultiColumnClassTable;
